import { DataObjectList, ObjectClassGenerator } from '../db/dataobject';
import { getMemoryTable, saveDbToRecoveryServer } from '../db/datamanager';
import type { Position, Feature } from 'geojson';
import logger from '../logger';
import SampleSite, { SampleSiteList } from './SampleSiteClass';
import Waypoint, { WaypointList } from './WaypointClass';
import Zone, { ZoneList } from './ZoneClass';
import SamplingSpec, { SamplingSpecList } from './SamplingSpecClass';
import Job, { JobList } from './JobClass';
import ZoneRecording, { ZoneRecordingList } from './ZoneRecordingClass';
import Session, { SessionList } from './SessionClass';

import {
  assign_id,
  to_kml,
  calculate_path,
  generate_lab_csvs,
  create_zone_cores,
  getCentroidPath,
  update_or_delete_boundary,
  update_path,
  to_shapefile,
  to_pdf,
  _to_shapefile,
  _to_kml,
  to_ros_msg,
  update_pullin,
  update_mission_details,
  is_zone_mission,
  generate_from_shapes,
  renumber_samples,
  add_sample,
  add_core,
  getBarcodes,
  getMissionName,
  getCoresTaken,
  reverse_path,
  skipUnsampled,
  unskipAllSkipped,
  calculate_path_restore_cores,
  applySmartPathTourToExistingCores,
  applyZoneSmartPathTourToExistingCores,
  update_parking_point,
  update_starting_point,
  update_end_point,
  deleteExtraCoresForSamples,
} from '../db_ops/mission_ops';
import {
  cleanSet,
  dedupeBy,
  distanceLatLon,
  distanceMeters,
  getDuplicatesByKey,
  isNumeric,
  normalizeDoubleEncodedJsonString,
} from '../utils';
import { getAllSampleBoxes, getAllSamples } from '../dataModelHelpers';
import Sample from './SampleClass';
import { HistoricalLayersServiceImpl } from '../services/historical-layers/HistoricalLayersService';
import {
  SampleChangeType,
  ConfirmColumn,
  CoordinatePoint,
  KMLFeature,
  RogoShapefile,
  SampleSource,
  IntermediateWaypointsMap,
  MultiPolygonCoords,
} from '../types/types';
import { FrameID } from '../types/rosmsg';
import { PRINTER_CONFIG } from '../components/robot/configs/PrinterConfig';
import SampleBox from './SampleBoxClass';
import { Coordinate } from 'ol/coordinate';
import { KmlFeatureType } from '../featureTypes';
import {
  DEFAULT_CORE_CLOSE_DISTANCE_METERS,
  DEFAULT_CORE_PRESENT_DISTANCE_FEET,
  DEG_PER_METER_LAT,
  DEG_PER_METER_LON,
  M_PER_FT,
  METERS_PER_DEG_LAT,
  METERS_PER_DEG_LON,
  REORDER_TOLERANCE_METERS,
  SMART_PATH_MIN_DISTANCE_TO_BOUNDARY_METERS_DEFAULT,
  UNDEFINED_SAMPLE_ID_PREFIX,
} from '../constants';
import { alertError, alertSuccess, alertWarn } from '../alertDispatcher';
import SoilCore from './SoilCoreClass';
import { dispatchMissionHistoricalMapLayersLoaded, dispatchMissionUpdated } from '../missionEvents';
import { dispatchCoresNeedUpdate, dispatchSamplesUpdated, dispatchZonesUpdated } from '../mapEvents';
import { ClosestCore as CloseCore, IMission } from './types';
import { ZoneType } from './ZoneTypesDatatype';

/**
 * A sample or core is submission-safe when it sits at least
 * SMART_PATH_MIN_DISTANCE_TO_BOUNDARY_METERS_DEFAULT inside every FIELD zone and
 * at least that far outside every UNSAFE zone — the same buffer the routing
 * boundary applies to both.
 */
function isPointSafeForSubmission(
  fieldZoneFeatures: Feature<GeoJSON.MultiPolygon>[],
  unsafeZoneFeatures: Feature<GeoJSON.MultiPolygon>[],
  lat: number,
  lon: number,
): boolean {
  const margin = SMART_PATH_MIN_DISTANCE_TO_BOUNDARY_METERS_DEFAULT;

  return (
    fieldZoneFeatures.every((zoneFeature) => isPointSafeInsideMultiPolygon(zoneFeature, lat, lon, margin)) &&
    unsafeZoneFeatures.every((zoneFeature) => isPointSafeOutsideMultiPolygon(zoneFeature, lat, lon, margin))
  );
}

const ZONE_TYPE_LABELS: Record<number, string> = {
  [ZoneType.FIELD]: 'field',
  [ZoneType.UNSAFE]: 'unsafe',
  [ZoneType.SLOW]: 'slow',
  [ZoneType.PULLIN]: 'pullin',
  [ZoneType.COLLECTED_FIELD]: 'collected field',
  [ZoneType.SAMPLE_ZONE]: 'sample',
  [ZoneType.VISUAL_ZONE]: 'visual',
};
import SoilDumpEvent, { SoilDumpEventList } from './SoilDumpEventClass';
import SampleZone from './SampleZoneClass';
import MissionKmlLoader from '../services/mission-loading/original/from-kml/MissionKmlLoader';
import PointsUpdater from '../services/PointsUpdater';
import { DB_SAVE_REASONS } from '../services/RecoveryServerDbManager';
import { flatten, orderBy } from 'lodash';
import { IKMLElement, KMLDocument } from '../kml';
import { NavigationPoint } from '../services/paths/types';
import SmartPathBuilder, { DebugGeoJsonMode, SmartPathBuildResult } from '../services/paths/SmartPathBuilder';
import { AutoCorePlacer, type AutoCorePlacerOptions, type CoreGridSegment } from '../services/zones/AutoCorePlacer';
import { resolveTurnRadius, WheelType } from '../services/paths/robotPath/turnRadius';
import { simulateRobotPath } from '../services/paths/robotPath/RobotPathClient';
import { DubinsWheelType } from './local_storage';
import KmlToXagConverter from '../services/KmlToXagConverter';
import PathReorderer from '../services/paths/PathReorderer';
import {
  bufferBoundaryPolygonsInward,
  expandPolygonOutward,
  generateCoreIdentifierFromCoordinates,
  glueBoundaryWithBridgesAndUnsafeZonesKeepAll,
  isPointSafeInsideMultiPolygon,
  isPointSafeOutsideMultiPolygon,
} from '../services/paths/utils';
import { processMultipolygonCoords } from '../services/BoundaryService';
import PolyManager from '../services/zones/PolyManager';
import { GeoDataUtilities } from '@rogoag/shared-ts';
import { matchesCentroid } from '../services/mission-loading/original/from-shapes/OriginalPointsOrZonesFileProcessor';
import Boundary from './BoundaryClass';
import { Polygon as OLPolygon, MultiPolygon as OLMultiPolygon } from 'ol/geom';
import { getCenter } from 'ol/extent';
import { toLonLat } from 'ol/proj';
import { HistoricalLayersService } from '../types/historical-layers';
import { getMapBoundsFromCenter } from '../utilities/map-utils';
import LocalAtRecord from './LocalAtRecordClass';
import { JobsFieldSet } from '@rogoag/airtable/crm';
import { getLocalAtSearch } from '../db_ops/airtable_search_ops';
import User from './UserClass';

export default class Mission extends ObjectClassGenerator<Mission>('Mission') implements IMission {
  // attributes
  #name: string;
  #last_modified: number;
  #job_id: string | undefined;
  #description: string;
  #pullin_lat: number;
  #pullin_lon: number;
  #parking_point: { lat: number; lon: number };
  #starting_point: { lat: number; lon: number };
  #end_point: { lat: number; lon: number };
  #sample_date: number;
  #nav_checksum: string;
  #settings_changes: string;
  #settings_start: string;
  #settings_end: string;
  #cores: string;
  #path_distance: number;
  #backup_samples: string;

  // relationships
  #Session_id?: number;

  static tableName = 'Mission';

  constructor(state = {}) {
    super(state);
    // publish persistent attributes
    // iterate through all of the keys of the IMission interface
    // and publish them as attributes
    // this is a shorthand way to avoid manually writing out each attribute
    // for example, this.publishAttribute(Mission, 'name');

    // type MissionKeys = keyof Omit<IMission, 'instance_id' | '_instance_id'>;
    // const keysToSkip = [
    //   '_instance_id',
    //   '_version',
    //   '_refs',
    // ];
    // for (const key of Object.keys(this) as Array<MissionKeys>) {
    //   if (keysToSkip.includes(key)) continue;
    //   if (typeof this[key] === 'function') continue;
    //   this.publishAttribute(Mission, key);
    // }
    this.publishAttribute(Mission, 'name');
    this.publishAttribute(Mission, 'last_modified');
    this.publishAttribute(Mission, 'job_id');
    this.publishAttribute(Mission, 'description');
    this.publishAttribute(Mission, 'pullin_lat');
    this.publishAttribute(Mission, 'pullin_lon');
    this.publishAttribute(Mission, 'parking_point');
    this.publishAttribute(Mission, 'starting_point');
    this.publishAttribute(Mission, 'end_point');
    this.publishAttribute(Mission, 'sample_date');
    this.publishAttribute(Mission, 'nav_checksum');
    this.publishAttribute(Mission, 'settings_changes');
    this.publishAttribute(Mission, 'settings_start');
    this.publishAttribute(Mission, 'settings_end');
    this.publishAttribute(Mission, 'cores');
    this.publishAttribute(Mission, 'path_distance');
    this.publishAttribute(Mission, 'backup_samples');

    this.publishAttribute(Mission, 'Session_id');
    // initialize state
    this.initializeState(state);
  }

  initializeState(state: Partial<Mission> = {}) {
    this._instance_id = state._instance_id!;
    this._refs = { ...state._refs };
    this._version = state._version!;
    this.#name = state.name || '';
    this.#last_modified = state.last_modified || 0.0;
    this.#job_id = state.job_id || '';
    this.#description = state.description || '';
    this.#pullin_lat = state.pullin_lat || 0.0;
    this.#pullin_lon = state.pullin_lon || 0.0;
    this.#parking_point = state.parking_point || { lat: 0, lon: 0 };
    this.#starting_point = state.starting_point || { lat: 0, lon: 0 };
    this.#end_point = state.end_point || { lat: 0, lon: 0 };
    this.#sample_date = state.sample_date || 0.0;
    this.#nav_checksum = state.nav_checksum || '';
    this.#settings_changes = state.settings_changes || '';
    this.#settings_start = state.settings_start || '';
    this.#settings_end = state.settings_end || '';
    this.#path_distance = state.path_distance || 0.0;
    this.#cores = state.cores || '';
    this.#backup_samples = state.backup_samples ?? '[]';
    this.#Session_id = state.Session_id;
  }

  dispose() {
    for (const samplesite of this.getSampleSites()) {
      samplesite.Mission_id = undefined;
      samplesite.dispose();
    }

    for (const waypoint of this.getWaypoints()) {
      waypoint.Mission_id = undefined;
      waypoint.dispose();
    }

    for (const zone of this.getZones()) {
      zone.Mission_id = undefined;
      zone.dispose();
    }

    for (const samplingspec of this.getSamplingSpecs()) {
      samplingspec.Mission_id = undefined;
      samplingspec.dispose();
    }

    const job = this.getJob();

    if (job) {
      job.Mission_id = undefined;
      job.dispose();
    }

    for (const zonerecording of this.getZoneRecordings()) {
      zonerecording.Mission_id = undefined;
      zonerecording.dispose();
    }

    const session = this.getSession();

    if (session) {
      this.Session_id = undefined;
    }

    Mission.delete(this.instance_id);
  }

  set name(value) {
    this.#name = value;
    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  get name() {
    return this.#name;
  }

  set last_modified(value) {
    this.#last_modified = value;
    this.syncToDB();
  }

  get last_modified() {
    return this.#last_modified;
  }

  set job_id(value) {
    this.#job_id = value;
    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  get job_id() {
    return this.#job_id;
  }

  set description(value) {
    this.#description = value;
    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  get description() {
    return this.#description;
  }

  set pullin_lat(value) {
    this.#pullin_lat = value;
    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  get pullin_lat() {
    return this.#pullin_lat;
  }

  set pullin_lon(value) {
    this.#pullin_lon = value;
    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  get pullin_lon() {
    return this.#pullin_lon;
  }

  set parking_point(value) {
    this.#parking_point = value;
    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  get parking_point() {
    return this.#parking_point;
  }

  static arePullinPointCoordinatesValid(lat: number, lon: number): boolean {
    return lat !== 0 && lon !== 0;
  }

  isPullinValid() {
    return Mission.arePullinPointCoordinatesValid(this.pullin_lat, this.pullin_lon);
  }

  setPullinByBoundary(): void {
    const field = this.getZones().find((sel) => sel.zone_type === ZoneType.FIELD);
    const field_bnd = field ? field.getOuterZoneBoundarys()[0] : undefined;

    if (field_bnd && field_bnd.coordinates.length > 0) {
      this.pullin_lat = field_bnd.coordinates[0][0];
      this.pullin_lon = field_bnd.coordinates[0][1];
    }
  }

  setPullinByFirstSampleOrCore(): void {
    const firstSample = cleanSet(
      orderBy(flatten(this.getSampleSites().map((site) => site.getSamples())), ['order'], ['asc']),
    )[0];

    if (!firstSample) {
      return;
    }

    if (firstSample.getSampleSite().getSampleCentroid()) {
      this.pullin_lat = firstSample.getSampleSite()?.getSampleCentroid()?.lat || 0;
      this.pullin_lon = firstSample.getSampleSite()?.getSampleCentroid()?.lon || 0;
    } else {
      const firstCore = cleanSet(orderBy(firstSample.getSoilCores(), ['core_number'], ['asc']))[0];

      if (firstCore) {
        this.pullin_lat = firstCore.lat;
        this.pullin_lon = firstCore.lon;
      }
    }
  }

  isParkingPointPresent(): boolean {
    return this.parking_point.lat !== 0 && this.parking_point.lon !== 0;
  }

  set starting_point(value) {
    this.#starting_point = value;
    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  get starting_point() {
    return this.#starting_point;
  }

  isStartingPointPresent(): boolean {
    return this.starting_point.lat !== 0 && this.starting_point.lon !== 0;
  }

  set end_point(value) {
    this.#end_point = value;
    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  isEndPointPresent(): boolean {
    return this.end_point.lat !== 0 && this.end_point.lon !== 0;
  }

  get end_point() {
    return this.#end_point;
  }

  set sample_date(value) {
    this.#sample_date = value;
    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  get sample_date() {
    return this.#sample_date;
  }

  set nav_checksum(value) {
    this.#nav_checksum = value;
    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  get nav_checksum() {
    return this.#nav_checksum;
  }

  get settings_start() {
    return this.#settings_start;
  }

  set settings_start(value: string) {
    this.#settings_start = value;
    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  get settings_end() {
    return this.#settings_end;
  }

  set settings_end(value: string) {
    this.#settings_end = value;
    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  set settings_changes(value) {
    this.#settings_changes = value;
    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  get settings_changes() {
    return this.#settings_changes;
  }

  get cores() {
    return this.#cores;
  }

  set cores(value: string) {
    this.#cores = value;
    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  get backup_samples() {
    return this.#backup_samples;
  }

  set backup_samples(value: string) {
    if (typeof value !== 'string') {
      throw new Error(`backup_samples must be a string`);
    }

    this.#backup_samples = value;
    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  get plot_boundary_collected(): boolean {
    if (!this.#job_id) {
      return false;
    }

    return localStorage.getItem(`plot_boundary_collected_${this.#job_id}`) === 'true';
  }

  set plot_boundary_collected(value: boolean) {
    if (!this.#job_id) {
      return;
    }

    if (value) {
      localStorage.setItem(`plot_boundary_collected_${this.#job_id}`, 'true');
    } else {
      localStorage.removeItem(`plot_boundary_collected_${this.#job_id}`);
    }
  }

  async replaceSampleWithBackup(
    sample: Sample,
    userName: string,
    changeReason: string,
    mode: 'sampling' | 'maps',
  ): Promise<void> {
    const backupSamples = JSON.parse(normalizeDoubleEncodedJsonString(this.backup_samples)) || [];

    if (!Array.isArray(backupSamples) || backupSamples.length === 0) {
      alertError('Mission: no backup samples found');

      return;
    }

    const backupSample = backupSamples.pop();

    await sample.replaceWithBackup(backupSample, {
      changeReason,
      mode,
      userName,
    });

    this.backup_samples = JSON.stringify(backupSamples);
  }

  get path_distance() {
    return this.#path_distance;
  }

  set path_distance(value: number) {
    this.#path_distance = value;
    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  get session_token() {
    return this.getSession()?.token;
  }

  get spec_count() {
    return this.getSamplingSpecs().length;
  }

  async clearSampleBarcodes() {
    for (const sample of this.getAllSamples()) {
      await sample.clearBarcode();
    }
  }

  resetPulled() {
    this.getSamples().forEach((sample) => sample.resetPulled());
  }

  addWaypoint(lat: number, lon: number, bearing_azimuth_deg?: number) {
    const waypointNumber = cleanSet(this.getWaypoints()).length + 1;
    const waypoint = Waypoint.create();

    waypoint.Mission_id = this.instance_id!;
    waypoint.waypoint_number = waypointNumber;
    waypoint.lat = lat;
    waypoint.lon = lon;
    waypoint.bearing_azimuth_deg = bearing_azimuth_deg;
  }

  getNextSampleNumber(): number {
    const sample_ids = this.getAllSamples().map((sample) => sample.sample_id);

    if (sample_ids.length === 0) {
      return 1;
    }

    if (sample_ids.some((sample_id) => Number.isNaN(parseInt(sample_id)))) {
      // parseInt and filter out NaNs
      const max_sample_id = Math.max(
        ...sample_ids.map((sample_id) => parseInt(sample_id)).filter((sample_id) => !Number.isNaN(sample_id)),
      );

      return max_sample_id + 1;
    }

    const max_sample_id = Math.max(...sample_ids.map((sample_id) => parseInt(sample_id)));

    return max_sample_id + 1;
  }

  getNextAvailableUndefinedSampleId(): string {
    const undefinedSamples = this.getAllSamples()
      .filter((s) => s.isUndefinedSample())
      .sort((a, b) => {
        const aNumber = Sample.extractNumberFromUndefinedSampleId(a.sample_id);
        const bNumber = Sample.extractNumberFromUndefinedSampleId(b.sample_id);

        // Ascending order
        return aNumber - bNumber;
      });

    const undefinedSamplesLength = undefinedSamples.length;

    return (
      UNDEFINED_SAMPLE_ID_PREFIX +
      (undefinedSamplesLength > 0
        ? Sample.extractNumberFromUndefinedSampleId(undefinedSamples[undefinedSamplesLength - 1].sample_id) + 1
        : '1')
    );
  }

  // @ts-ignore
  getSampleSites() {
    if (this._refs && this._refs.SampleSite) {
      return new SampleSiteList(
        ...Array.from(this._refs.SampleSite)
          .map((id) => SampleSite.get(id))
          .filter((sel) => !!sel),
      );
    } else {
      const sampleSites = SampleSite.query((sel) => sel && sel.Mission_id === this.instance_id);

      for (const sampleSite of sampleSites) {
        sampleSite.Mission_id = this.instance_id; // re-set foreign key to force update of _refs
      }

      return new SampleSiteList(...sampleSites);
    }
  }

  createSampleSite(originalSampleId: string): SampleSite {
    const sampleSite = SampleSite.create();
    sampleSite.original_sample_id = SampleSite.validateOriginalSampleId(this, originalSampleId);
    sampleSite.Mission_id = this.instance_id;

    return sampleSite;
  }

  getSampleZoneWithOriginalSampleID(sampleID: string): SampleZone | undefined {
    const sampleZones = cleanSet(this.getSampleSites().map((sel) => sel.getSampleZone()));

    return sampleZones.find((sel) => {
      if (!sel) {
        return false;
      }

      const sampleSite = sel.getSampleSite();

      if (!sampleSite) {
        return false;
      }

      return sampleSite.original_sample_id === sampleID;
    });
  }

  validateBeforeSamplingSubmission(): { preventSubmission: boolean; errors: string[] } {
    const errors: string[] = [];
    let preventSubmission: boolean = false;

    const duplicateSamples = this.getDuplicateSamples();

    if (duplicateSamples.length) {
      errors.push(`Duplicate sample ids found: ${[...new Set(duplicateSamples.map((s) => s.sample_id))].join(', ')}`);

      preventSubmission = true;
    }

    return {
      preventSubmission,
      errors,
    };
  }

  validateBeforeBoundaryApprovalSubmission(): {
    preventSubmission: boolean;
    errors: string[];
    defectiveGeometries: Feature<GeoJSON.Point | GeoJSON.Polygon>[];
    warningGeometries: Feature<GeoJSON.Point | GeoJSON.Polygon>[];
  } {
    const errors: string[] = [];
    let preventSubmission: boolean = false;
    const defectiveGeometries: Feature<GeoJSON.Point | GeoJSON.Polygon>[] = [];
    const warningGeometries: Feature<GeoJSON.Point | GeoJSON.Polygon>[] = [];

    // --- check field zone validity ---

    const fieldZonesValidation = this.areFieldZonesValid();

    if (!fieldZonesValidation.isValid) {
      errors.push(
        `Field boundary is invalid: check for self-intersections, wrongly-placed holes, overlaps and so on...`,
      );

      preventSubmission = true;

      if (fieldZonesValidation.defectiveGeometries.length > 0) {
        defectiveGeometries.push(...fieldZonesValidation.defectiveGeometries);
      }
    }

    if (fieldZonesValidation.warningGeometries.length > 0) {
      errors.push(`Attention: field boundary has some warnings`);
      warningGeometries.push(...fieldZonesValidation.warningGeometries);
    }

    const overlappingFieldZonePolyIds = this.getOverlappingFieldZonePolyIds();

    if (overlappingFieldZonePolyIds.length > 0) {
      errors.push(`Field boundary polygons are overlapping: ${overlappingFieldZonePolyIds.join(', ')}`);

      preventSubmission = true;
    }

    // --- check collected zone validity ---

    const collectedFieldZonesValidation = this.areCollectedFieldZonesValid();

    if (!collectedFieldZonesValidation.isValid) {
      errors.push(
        `Collected field boundary is invalid: ${collectedFieldZonesValidation.message || 'either missing or has self-intersections, wrongly-placed holes, overlaps and so on...'}`,
      );

      preventSubmission = true;

      if (collectedFieldZonesValidation.defectiveGeometries.length > 0) {
        defectiveGeometries.push(...collectedFieldZonesValidation.defectiveGeometries);
      }
    }

    if (collectedFieldZonesValidation.warningGeometries.length > 0) {
      errors.push(`Attention: collected field boundary has some warnings`);
      warningGeometries.push(...collectedFieldZonesValidation.warningGeometries);
    }

    return {
      preventSubmission,
      errors,
      defectiveGeometries,
      warningGeometries,
    };
  }

  validateBeforeMapMakingSubmission(): {
    preventSubmission: boolean;
    errors: string[];
    defectiveGeometries: Feature<GeoJSON.Point | GeoJSON.Polygon>[];
    warningGeometries: Feature<GeoJSON.Point | GeoJSON.Polygon | GeoJSON.LineString>[];
  } {
    const errors: string[] = [];
    let preventSubmission: boolean = false;
    const defectiveGeometries: Feature<GeoJSON.Point | GeoJSON.Polygon>[] = [];
    const warningGeometries: Feature<GeoJSON.Point | GeoJSON.Polygon | GeoJSON.LineString>[] = [];

    // --- pull-in validity ---

    if (!this.isPullinValid()) {
      errors.push(`Pullin is not valid: ${this.pullin_lat}, ${this.pullin_lon}`);

      preventSubmission = true;
    }

    // --- check for absolutely wrong boundaries ---

    const zonesWithAbsolutelyInvalidBoundaries = dedupeBy(
      this.getZones().filter((zone: Zone) => !zone.hasValidBoundaries()),
      'zone_name',
    );

    if (zonesWithAbsolutelyInvalidBoundaries.length) {
      errors.push(
        `Found ${zonesWithAbsolutelyInvalidBoundaries.length} zone(s) with invalid boundaries (Zone IDs: ${zonesWithAbsolutelyInvalidBoundaries.map((zone: Zone) => zone.zone_name).join(', ')})`,
      );

      preventSubmission = true;
    }

    // --- check for samples with invalid names ---

    const samplesWithInvalidNames = this.getSamplesWithInvalidSampleIds();

    if (samplesWithInvalidNames.length) {
      errors.push(
        `Found ${samplesWithInvalidNames.length} samples(s) with invalid ids: ${samplesWithInvalidNames.map((sample) => sample.sample_id).join(', ')}`,
      );

      preventSubmission = true;
    }

    // --- check field zone validity ---

    const fieldZonesValidation = this.areFieldZonesValid();

    if (!fieldZonesValidation.isValid) {
      errors.push(
        `Field boundary is invalid: check for self-intersections, wrongly-placed holes, overlaps and so on...`,
      );

      if (fieldZonesValidation.defectiveGeometries.length > 0) {
        defectiveGeometries.push(...fieldZonesValidation.defectiveGeometries);
      }

      preventSubmission = true;
    }

    if (fieldZonesValidation.warningGeometries.length > 0) {
      errors.push(`Attention: field boundary has some warnings`);
      warningGeometries.push(...fieldZonesValidation.warningGeometries);
    }

    const overlappingFieldZonePolyIds = this.getOverlappingFieldZonePolyIds();

    if (overlappingFieldZonePolyIds.length > 0) {
      errors.push(`Field boundary polygons are overlapping: ${overlappingFieldZonePolyIds.join(', ')}`);
    }

    // --- Collected field doesn't have to be present in mapmaking, so we only check their validity if they exist ---

    const collectedFieldZones = this.getCollectedFieldZones();

    if (collectedFieldZones.length > 0) {
      const collectedFieldZonesValidation = this.areCollectedFieldZonesValid();

      if (!collectedFieldZonesValidation.isValid) {
        errors.push(
          `Collected field boundary is invalid: ${collectedFieldZonesValidation.message || 'either missing or has self-intersections, wrongly-placed holes, overlaps and so on...'}`,
        );

        preventSubmission = true;

        // Append collected field defective geometries
        if (collectedFieldZonesValidation.defectiveGeometries.length > 0) {
          defectiveGeometries.push(...collectedFieldZonesValidation.defectiveGeometries);
        }
      }

      if (collectedFieldZonesValidation.warningGeometries.length > 0) {
        errors.push(`Collected field boundary has some warnings`);
        warningGeometries.push(...collectedFieldZonesValidation.warningGeometries);
      }
    }

    // --- checking unsafe, slow, and pullin zones for validity ---

    [ZoneType.UNSAFE, ZoneType.SLOW, ZoneType.PULLIN].forEach((zoneType: ZoneType) => {
      // We want to be pretty laxed here allowing big intersection areas
      // because we mostly care about self-intersections (bowties).
      const zoneValidation = this.validateZonesByType(zoneType, 10000, false);

      if (!zoneValidation.isValid) {
        errors.push(zoneValidation.message ?? `Zone ${ZONE_TYPE_LABELS[zoneType]} is invalid`);

        if (zoneValidation.defectiveGeometries.length > 0) {
          defectiveGeometries.push(...zoneValidation.defectiveGeometries);
        }

        preventSubmission = true;
      }

      if (zoneValidation.warningGeometries.length > 0) {
        errors.push(`Zone ${ZONE_TYPE_LABELS[zoneType]} has some warnings`);

        warningGeometries.push(...zoneValidation.warningGeometries);
      }
    });

    // --- samples witn no cores check ---

    const sampleWithNoCores = this.getSamplesWithNoCores();

    if (sampleWithNoCores.length) {
      errors.push(
        `Found ${sampleWithNoCores.length} sample(s) with no cores: ${sampleWithNoCores.map((s) => s.sample_id).join(', ')}`,
      );

      preventSubmission = true;
    }

    // --- pending cores without a waypoint ---

    const pendingCoresWithoutWaypoint = this.getPendingCoresWithoutWaypoint();

    if (pendingCoresWithoutWaypoint.length) {
      errors.push(
        `Found ${pendingCoresWithoutWaypoint.length} core(s) without a waypoint: ${pendingCoresWithoutWaypoint.map((c) => c.name).join(', ')}`,
      );

      preventSubmission = true;
    }

    // --- duplicate samples ---

    const duplicateSamples = this.getDuplicateSamples();

    if (duplicateSamples.length) {
      errors.push(`Duplicate sample ids found: ${[...new Set(duplicateSamples.map((s) => s.sample_id))].join(', ')}`);

      preventSubmission = true;
    }

    const duplicateSampleSites = this.getDuplicateSampleSites();

    if (duplicateSampleSites.length) {
      errors.push(
        `Duplicate sample sites found: ${[...new Set(duplicateSampleSites.map((ss) => ss.original_sample_id))].join(', ')}`,
      );

      preventSubmission = true;
    }

    // --- samles not safe inside the boundary ---

    const samplesNotSafeInsideBoundary = this.getSamplesNotSafeInsideBoundary();

    if (samplesNotSafeInsideBoundary.length) {
      errors.push(
        `Samples not safe inside field boundary: ${samplesNotSafeInsideBoundary.map((s) => s.sample_id).join(', ')}`,
      );
    }

    if (!this.is_zone_mission()) {
      return {
        preventSubmission,
        errors,
        defectiveGeometries,
        warningGeometries,
      };
    }

    // -----------------------------------------
    // --- Zone mission specific validations ---
    // -----------------------------------------

    // -- zone cores not safe inside boundary ---

    const zoneCoresNotSafeInsideBoundary = this.getCoresNotSafeInsideBoundary();

    if (zoneCoresNotSafeInsideBoundary.length) {
      errors.push(
        `Cores not safe inside field boundary: ${zoneCoresNotSafeInsideBoundary.map((core) => core.name).join(', ')}`,
      );
    }

    // -- interleaving in non-interleave mission ---

    if (!this.is_zone_interleave_mission()) {
      const interLeavingSamples = this.interLeavingSamples();

      if (interLeavingSamples.length) {
        errors.push(`The path has interleaving zones: ${interLeavingSamples.join(', ')}.`);
      }
    }

    // --- zones with no cores ---

    const zonesWithNoCores = this.getSampleZonesWithNoCores();

    if (zonesWithNoCores.length) {
      errors.push(
        `Found ${zonesWithNoCores.length} zone(s) with no cores: ${zonesWithNoCores.map((zone) => zone.zone_name).join(', ')}`,
      );

      preventSubmission = true;
    }

    // --- samples with no zone ---

    const samplesWithNoZone = this.getAllSamples().filter((sample) => {
      const sampleZone = this.getSampleZoneWithSampleID(sample.sample_id);

      return sampleZone === undefined || !sampleZone.getZone();
    });

    if (samplesWithNoZone.length) {
      errors.push(
        `Found ${samplesWithNoZone.length} sample(s) with no zone: ${samplesWithNoZone.map((sample) => sample.sample_id).join(', ')}`,
      );

      preventSubmission = true;
    }

    // --- cores belonging to multiple sample zones ---

    const coresBelongingToMultipleSampleZones = this.getCoresBelongingToMultipleSampleZonesGeographically();

    if (coresBelongingToMultipleSampleZones.length) {
      errors.push(
        `There are cores that belong to multiple sample zones geographically: ${coresBelongingToMultipleSampleZones.map((c) => c.name).join(', ')}`,
      );

      preventSubmission = true;
    }

    // --- cores not belonging to any sample zone geographically ---

    const coresNotBelongingToAnySampleZoneGeographically = this.getCoresNotBelongingToAnySampleZoneGeographically();

    if (coresNotBelongingToAnySampleZoneGeographically.length) {
      errors.push(
        `There are cores that do not belong to any sample zones geographically: ${coresNotBelongingToAnySampleZoneGeographically.map((c) => c.name).join(', ')}`,
      );

      preventSubmission = true;
    }

    return {
      preventSubmission,
      errors,
      defectiveGeometries,
      warningGeometries,
    };
  }

  getSampleZoneWithSampleID(sampleID: string): SampleZone | undefined {
    const sampleZones = cleanSet(this.getSampleSites().map((sel) => sel.getSampleZone()));

    return sampleZones.find((sel) => {
      if (!sel) {
        return false;
      }

      const sampleSite = sel.getSampleSite();

      if (!sampleSite) {
        return false;
      }

      return sampleSite?.getSamples()[0]?.sample_id === sampleID;
    });
  }

  getOverlappingFieldZonePolyIds(): string[] {
    const out = new Set<string>();

    this.getFieldZones().forEach((fieldZone) => {
      fieldZone.getOverlappingPolyIds().forEach((p) => out.add(p));
    });

    return Array.from(out);
  }

  areFieldZonesValid() {
    return this.validateZonesByType(ZoneType.FIELD);
  }

  areCollectedFieldZonesValid() {
    return this.validateZonesByType(ZoneType.COLLECTED_FIELD);
  }

  private validateZonesByType(
    zoneType: ZoneType,
    maxAllowedIntersectionArea: number = 100,
    required: boolean = true,
  ): {
    isValid: boolean;
    defectiveGeometries: Feature<GeoJSON.Point | GeoJSON.Polygon>[];
    warningGeometries: Feature<GeoJSON.Point | GeoJSON.Polygon>[];
    message?: string;
  } {
    const zones = cleanSet(this.getZones()).filter((zone) => zone.zone_type === zoneType);
    const label = ZONE_TYPE_LABELS[zoneType] ?? 'unknown';

    if (zones.length === 0) {
      const isValid = !required;

      return {
        isValid,
        message: isValid ? undefined : `No ${label} zones`,
        defectiveGeometries: [],
        warningGeometries: [],
      };
    }

    const allDefectiveGeometries: Feature<GeoJSON.Point | GeoJSON.Polygon>[] = [];
    const allWarningGeometries: Feature<GeoJSON.Point | GeoJSON.Polygon>[] = [];

    for (const zone of zones) {
      const validationResult = zone.validateBoundaries(true, maxAllowedIntersectionArea);

      if (!validationResult.isValid) {
        if (validationResult.defectiveGeometries) {
          allDefectiveGeometries.push(...validationResult.defectiveGeometries);
        }
      }

      if (validationResult.warningGeometries) {
        allWarningGeometries.push(...validationResult.warningGeometries);
      }
    }

    if (allDefectiveGeometries.length > 0) {
      return {
        isValid: false,
        defectiveGeometries: allDefectiveGeometries,
        warningGeometries: allWarningGeometries,
        message: `Found ${allDefectiveGeometries.length} geometry error(s) across ${label} zones`,
      };
    }

    return { isValid: true, defectiveGeometries: [], warningGeometries: allWarningGeometries };
  }

  getCoresBelongingToMultipleSampleZonesGeographically() {
    return this.getAllCores().filter((core) => core.getSampleZonesThatMatchGeographically().length > 1);
  }

  getCoresNotBelongingToAnySampleZoneGeographically() {
    return this.getAllCores().filter((core) => core.getSampleZonesThatMatchGeographically().length === 0);
  }

  getCoresWithoutWaypoint() {
    return this.getAllCores().filter((core) => core.waypoint_number === undefined);
  }

  getPendingCoresWithoutWaypoint() {
    return this.getCoresWithoutWaypoint().filter((core) => core.isPending());
  }

  getDuplicateSamples() {
    return getDuplicatesByKey(this.getSamples(), 'sample_id');
  }

  getDuplicateSampleSites() {
    return getDuplicatesByKey(this.getSampleSites(), 'original_sample_id');
  }

  getSoilDumpEvents() {
    if (this._refs && this._refs.SoilDumpEvent) {
      return new SoilDumpEventList(
        ...Array.from(this._refs.SoilDumpEvent)
          .map((id) => SoilDumpEvent.get(id))
          .filter((sel) => !!sel),
      );
    } else {
      const soilDumpEvents = SoilDumpEvent.query((sel) => sel && sel.Mission_id === this.instance_id);

      for (const soildumpevent of soilDumpEvents) {
        soildumpevent.Mission_id = this.instance_id; // re-set foreign key to force update of _refs
      }

      return new SoilDumpEventList(...soilDumpEvents);
    }
  }

  // @ts-ignore
  getSamples() {
    return this.getSampleSites().getSamples();
  }

  // Per-instance memoised result of getAllCores. Position updates call this
  // at 10 Hz; a large field has ~150 samples × ~8 cores ≈ 1200 cores, and
  // recomputing it on every position message was one of the dominant CPU
  // costs we measured (Rose 6: 127 samples / ~1016 cores showed visible
  // lag on Shift+C).
  //
  // The result depends on three tables walked by getSamples().getSoilCores():
  //   1. SampleSite (which sites belong to this mission)
  //   2. Sample     (which samples belong to those sites; rename / move / add / delete)
  //   3. SoilCore   (which cores belong to those samples; add / move / delete)
  //
  // We invalidate by comparing each table's mutationCount in O(1).
  #allCoresCache?: {
    result: SoilCore[];
    soilCoreMutations: number;
    sampleMutations: number;
    sampleSiteMutations: number;
  };

  getAllCores(): SoilCore[] {
    const soilCoreTable = getMemoryTable('SoilCore');
    const sampleTable = getMemoryTable('Sample');
    const sampleSiteTable = getMemoryTable('SampleSite');

    if (
      this.#allCoresCache &&
      this.#allCoresCache.soilCoreMutations === (soilCoreTable?.mutationCount ?? 0) &&
      this.#allCoresCache.sampleMutations === (sampleTable?.mutationCount ?? 0) &&
      this.#allCoresCache.sampleSiteMutations === (sampleSiteTable?.mutationCount ?? 0)
    ) {
      return this.#allCoresCache.result;
    }

    const result = this.getSamples().reduce((accumulator: SoilCore[], currentValue: Sample) => {
      accumulator.push(...currentValue.getSoilCores());

      return accumulator;
    }, []);

    // Capture counters AFTER the rebuild. Some lazy-init paths
    // (e.g. Sample.getSoilCores re-setting foreign keys to populate
    // _refs on first read) mutate the SoilCore table during the
    // rebuild itself, so capturing them up-front would always under-
    // count and cause an immediate cache miss on the next call.
    this.#allCoresCache = {
      result,
      soilCoreMutations: soilCoreTable?.mutationCount ?? 0,
      sampleMutations: sampleTable?.mutationCount ?? 0,
      sampleSiteMutations: sampleSiteTable?.mutationCount ?? 0,
    };

    return result;
  }

  getAllCoresSortedByWaypointNumber(): SoilCore[] {
    return this.getAllCores().sort(SoilCore.sortByWaypointNumber);
  }

  // Cached "safe routing area" — boundary buffered inward by
  // SMART_PATH_MIN_DISTANCE_TO_BOUNDARY_METERS_DEFAULT, unioned with pullin
  // zones (which bridge disjoint parts of a multipolygon field), with unsafe
  // zones (expanded outward by the same margin) subtracted. Anything outside
  // this polygon is either outside the boundary, within the safety buffer of
  // the boundary, or within the safety buffer of an unsafe zone. Used by both
  // the RoutingBoundaryLayer and the Dubin's-path validator.
  //
  // Depends on Zone (field / unsafe / pullin zones — add / delete / type)
  // AND Boundary (polygon vertex edits go through Boundary.coordinates
  // setter, which bumps Boundary — not Zone — mutationCount).
  //
  // We use the *keep-all* glue variant: the visualization should show every
  // disjoint safe area, not just the largest connected piece. SmartPathBuilder
  // collapses to a single polygon (it can only route within one connected
  // region), but here a user expects to see all parts of their multipolygon
  // field even if buffering / unsafe-zone subtraction splits the result.
  #safeRoutingBoundaryCache?: {
    result: Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon> | null;
    zoneMutations: number;
    boundaryMutations: number;
  };

  getSafeRoutingBoundary(): Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon> | null {
    const zoneTable = getMemoryTable('Zone');
    const boundaryTable = getMemoryTable('Boundary');
    const zoneMutations = zoneTable?.mutationCount ?? 0;
    const boundaryMutations = boundaryTable?.mutationCount ?? 0;

    if (
      this.#safeRoutingBoundaryCache &&
      this.#safeRoutingBoundaryCache.zoneMutations === zoneMutations &&
      this.#safeRoutingBoundaryCache.boundaryMutations === boundaryMutations
    ) {
      return this.#safeRoutingBoundaryCache.result;
    }

    let result: Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon> | null = null;

    try {
      const boundaryZone = this.getBoundary();

      if (boundaryZone) {
        const boundaryFeature = boundaryZone.toMultiPolygon();
        // allowSplit: a pinched boundary ring (e.g. two lobes joined by a
        // sub-metre neck) legitimately yields several inset components. The
        // safe-routing area must show every one of them; dropping the polygon
        // would also tell /path/simulate that the whole field is out of bounds.
        const bufferedBoundary = bufferBoundaryPolygonsInward(
          boundaryFeature,
          SMART_PATH_MIN_DISTANCE_TO_BOUNDARY_METERS_DEFAULT,
          [],
          { allowSplit: true },
        );
        const bridges = this.getPullinZones().map((z) => z.toMultiPolygon());
        const expandedUnsafeZones = this.getUnsafeZones()
          .map((z) => expandPolygonOutward(z.toMultiPolygon(), SMART_PATH_MIN_DISTANCE_TO_BOUNDARY_METERS_DEFAULT))
          .filter((z): z is Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon> => z !== null);

        result = glueBoundaryWithBridgesAndUnsafeZonesKeepAll(bufferedBoundary, bridges, expandedUnsafeZones);
      }
    } catch (e) {
      console.warn('getSafeRoutingBoundary failed:', e);
      result = null;
    }

    // Capture AFTER rebuild — matches active-record pattern (see getAllCores).
    this.#safeRoutingBoundaryCache = {
      result,
      zoneMutations: zoneTable?.mutationCount ?? 0,
      boundaryMutations: boundaryTable?.mutationCount ?? 0,
    };

    return result;
  }

  getAllCoresSortedBySampleOrderAndCoreNumber(): SoilCore[] {
    return this.getAllCores().sort(SoilCore.sortBySampleOrderAndCoreNumber);
  }

  getSamplesWithInvalidSampleIds(): Sample[] {
    return this.getAllSamples().filter((sample) => {
      if (sample.isUndefinedSample()) {
        return true;
      }

      const match = matchesCentroid(sample.sample_id);

      return !match;
    });
  }

  getSamplesNotSafeInsideBoundary(): Sample[] {
    const fieldZones = this.getFieldZones();

    if (fieldZones.length === 0) {
      return [];
    }

    const fieldZoneFeatures = fieldZones.map((z) => z.toMultiPolygon());
    const unsafeZoneFeatures = this.getUnsafeZones().map((z) => z.toMultiPolygon());

    return this.getAllSamples().filter((sample) => {
      const centroid = sample.getSampleCentroid();
      const points: { lat: number; lon: number }[] = centroid ? [{ lat: centroid.lat, lon: centroid.lon }] : [];

      // Zone missions get per-core coverage from getCoresNotSafeInsideBoundary().
      // A grid mission drives a sample as a pair of endpoints (first + last
      // pending core) and can hold dozens of cores, so the centroid plus those
      // two is the coverage that matters — without flagging every middle core.
      if (!this.is_zone_mission()) {
        const drivenCores = [...sample.getSoilCores()]
          .filter((core) => core.getCorePoint()?.getWaypoint())
          .sort((a, b) => a.core_number - b.core_number);
        const endpoints = [drivenCores[0], drivenCores[drivenCores.length - 1]].filter(Boolean);

        for (const core of endpoints) {
          points.push({ lat: core.lat, lon: core.lon });
        }
      }

      return points.some(({ lat, lon }) => !isPointSafeForSubmission(fieldZoneFeatures, unsafeZoneFeatures, lat, lon));
    });
  }

  getCoresNotSafeInsideBoundary(): SoilCore[] {
    const fieldZones = this.getFieldZones();

    if (fieldZones.length === 0) {
      return [];
    }

    const fieldZoneFeatures = fieldZones.map((z) => z.toMultiPolygon());
    const unsafeZoneFeatures = this.getUnsafeZones().map((z) => z.toMultiPolygon());

    return this.getAllCores().filter(
      (core) => !isPointSafeForSubmission(fieldZoneFeatures, unsafeZoneFeatures, core.lat, core.lon),
    );
  }

  /**
   * Drive the mission on the robot's own model (pathfinder's /path/simulate) and
   * report what it does wrong: one error per hop that leaves the field boundary
   * and per avoidable loop.
   *
   * Advisory: the caller shows these and asks for confirmation, never blocks. A
   * pathfinder outage comes back as a single `warnings` entry so the mapmaker
   * knows the check did not run.
   *
   * No geometries: the offending hops are already drawn by the map's Robot path
   * overlay (orange for out-of-bounds, a marker per avoidable loop), so they do
   * not belong in the geometry-errors layer.
   */
  async validateRobotPath(): Promise<{ errors: string[]; warnings: string[] }> {
    const wheelType = DubinsWheelType.get() as WheelType;

    let result;

    try {
      result = await simulateRobotPath(this, resolveTurnRadius(wheelType));
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'Unknown error';

      console.warn('validateRobotPath - /path/simulate failed:', error);

      return { errors: [], warnings: [`Robot path check unavailable: ${detail}`] };
    }

    const errors: string[] = [];

    // inBounds is judged with margin 0 against the safe-routing polygon the
    // drive model was sent (RobotPathClient sends 0 + that polygon), so false
    // means the driven path leaves the clearance the mapmaker chose — the same
    // verdict the planner validated against (pathfinder's loop_repair.cpp:361
    // convention). This is the safety check the switch to /path/simulate exists
    // for.
    for (const hop of result.hops) {
      if (!hop.inBounds) {
        errors.push(
          `Robot path leaves the safe routing boundary between waypoints ${hop.fromIndex} and ${hop.toIndex}`,
        );
      }
    }

    for (const loop of result.loops) {
      if (loop.kind !== 'avoidable') {
        continue; // grid cores fix both headings — nothing to be done about it
      }

      errors.push(`Robot path loops (${loop.turnDeg}°) into waypoint ${loop.toIndex} — avoidable`);
    }

    return { errors, warnings: [] };
  }

  getSoilCoreByName(name: string): SoilCore | undefined {
    return this.getAllCores().find((core) => core.name === name);
  }

  getNextSampleInDriveOrder() {
    const samples = this.getSamples();
    const unsampledSamples = samples.filter((sample) => !sample.skipped_or_deleted && !sample.bag_id);

    if (unsampledSamples.length === 0) {
      return undefined;
    }

    const nextSample = unsampledSamples.sort((a, b) => a.order - b.order)[0];

    return nextSample;
  }

  #coresInBucketCache?: {
    result: SoilCore[];
    excludeBd: boolean;
    soilCoreMutations: number;
    soilDumpMutations: number;
  };

  getSoilCoresInBucket(excludeBdCores: boolean = true, allMissionCores?: SoilCore[]) {
    const soilCoreTable = getMemoryTable('SoilCore');
    const soilDumpTable = getMemoryTable('SoilDumpEvent');

    if (
      this.#coresInBucketCache &&
      this.#coresInBucketCache.excludeBd === excludeBdCores &&
      this.#coresInBucketCache.soilCoreMutations === (soilCoreTable?.mutationCount ?? 0) &&
      this.#coresInBucketCache.soilDumpMutations === (soilDumpTable?.mutationCount ?? 0)
    ) {
      return this.#coresInBucketCache.result;
    }

    const coreUndumped = (core: SoilCore) => {
      if (!core || !core.isInBucket()) {
        return false;
      }

      if (core.isPulledBDCore() && excludeBdCores) {
        return false;
      }

      return true;
    };

    const cores: SoilCore[] = allMissionCores !== undefined ? allMissionCores : this.getSamples().getSoilCores();
    const result = [...cores.filter(coreUndumped), ...this.getTestCores().filter(coreUndumped)];

    // Capture AFTER rebuild
    this.#coresInBucketCache = {
      result,
      excludeBd: excludeBdCores,
      soilCoreMutations: soilCoreTable?.mutationCount ?? 0,
      soilDumpMutations: soilDumpTable?.mutationCount ?? 0,
    };

    return result;
  }

  getLastDump() {
    return this.getSoilDumpEvents().sort((a, b) => b.timestamp - a.timestamp)[0];
  }

  getTestCores() {
    if (this._refs && this._refs.SoilCore) {
      return Array.from(this._refs.SoilCore)
        .map((id) => SoilCore.get(id))
        .filter((sel) => !!sel);
    } else {
      const soilCores = SoilCore.query((sel) => sel && sel.test_core_Mission_id === this.instance_id);

      for (const soilCore of soilCores) {
        soilCore.test_core_Mission_id = this.instance_id; // re-set foreign key to force update of _refs
      }

      return [...soilCores];
    }
  }

  getBoxes() {
    // Get all samples
    const samples = this.getAllSamples();

    // Get all box_uids from all samples with duplicates removed
    const boxUids = [...new Set(samples.map((sample) => sample.box_uid))];

    // Get all boxes with box_uids from boxIds
    const boxes = SampleBox.query((sel) => sel && boxUids.includes(sel.uid));

    return boxes;
  }

  getWaypoints() {
    if (this._refs && this._refs.Waypoint) {
      return new WaypointList(
        ...Array.from(this._refs.Waypoint)
          .map((id) => Waypoint.get(id))
          .filter((sel) => !!sel),
      );
    } else {
      const waypoints = Waypoint.query((sel) => sel && sel.Mission_id === this.instance_id);

      for (const waypoint of waypoints) {
        waypoint.Mission_id = this.instance_id; // re-set foreign key to force update of _refs
      }

      return new WaypointList(...waypoints);
    }
  }

  getUnskippedWaypoints() {
    const waypoints = this.getWaypoints();

    return waypoints.filter((wp) => {
      const sample = wp.getRelatedSample();

      if (!sample) {
        return false;
      }

      return !sample.skipped_or_deleted;
    });
  }

  getZoneById(zoneId: number) {
    const zones = this.getZones();

    return zones.find((zone) => zone.instance_id === zoneId);
  }

  getZones() {
    if (this._refs && this._refs.Zone) {
      return new ZoneList(
        ...Array.from(this._refs.Zone)
          .map((id) => Zone.get(id))
          .filter((sel) => !!sel),
      );
    } else {
      const zones = Zone.query((sel) => sel && sel.Mission_id === this.instance_id);

      for (const zone of zones) {
        zone.Mission_id = this.instance_id; // re-set foreign key to force update of _refs
      }

      return new ZoneList(...zones);
    }
  }

  getFieldZones() {
    return cleanSet(this.getZones()).filter((zone) => zone.zone_type === ZoneType.FIELD);
  }

  getUnsafeZones() {
    return cleanSet(this.getZones()).filter((zone) => zone.zone_type === ZoneType.UNSAFE);
  }

  getPullinZones() {
    return cleanSet(this.getZones()).filter((zone) => zone.zone_type === ZoneType.PULLIN);
  }

  getSlowZones() {
    return cleanSet(this.getZones()).filter((zone) => zone.zone_type === ZoneType.SLOW);
  }

  getCollectedFieldZones() {
    return cleanSet(this.getZones()).filter((zone) => zone.zone_type === ZoneType.COLLECTED_FIELD);
  }

  getZonesOfTypeSampleZone() {
    return cleanSet(this.getZones()).filter((zone) => zone.zone_type === ZoneType.SAMPLE_ZONE);
  }

  getBoundary(): Zone | undefined {
    const zones = this.getZones();

    if (zones.length === 0) {
      return undefined;
    }

    const collectedBoundary = zones.find((zone) => zone.zone_type === ZoneType.COLLECTED_FIELD);

    if (collectedBoundary) {
      return collectedBoundary;
    }

    const originalBoundary = zones.find((zone) => zone.zone_type === ZoneType.FIELD);

    if (originalBoundary) {
      return originalBoundary;
    }

    throw new Error(`Mission ${this.instance_id} does not have a boundary`);
  }

  /**
   * Get the center point and bounds of the mission
   * Returns lon/lat coordinates in EPSG:4326 and bounds
   * @returns Object containing center coordinates and bounds, or null if no field zone
   */
  getMissionCenter(options?: { fixedBoundsRadiusKm?: number }): {
    center: [number, number]; // [lon, lat]
    bounds: { xmin: number; ymin: number; xmax: number; ymax: number };
  } {
    // Get field zone to determine mission center
    const fieldZone = this.getZones().find((sel) => sel.zone_type === ZoneType.FIELD);
    const fixedRadiusKm = options?.fixedBoundsRadiusKm;
    const outerBoundaries = fieldZone?.to_feature();

    if (!fieldZone || outerBoundaries?.length === 0) {
      // Fallback to pullin point if no field boundaries
      console.warn('No field zone found, using pullin point');
      const lon = this.pullin_lon;
      const lat = this.pullin_lat;

      // Calculate bounds around pullin point
      const radiusKm = fixedRadiusKm ?? 1; // Default 1km radius = 2km x 2km total area
      const bounds = getMapBoundsFromCenter(lon, lat, radiusKm);

      return {
        center: [lon, lat],
        bounds: bounds,
      };
    }

    const fields = outerBoundaries!.map((feature) => {
      const coords = feature.geometry.coordinates.map((coords) =>
        coords.map((ring) => {
          return ring;
        }),
      );

      return new OLPolygon(coords);
    });

    const field = new OLMultiPolygon(fields);
    const fieldExtent = field.getExtent();
    const centerPoint = getCenter(fieldExtent);

    // Convert from EPSG:3857 (Web Mercator) to EPSG:4326 (lon/lat)
    const [lon, lat] = toLonLat(centerPoint);

    // If fixedBoundsRadiusKm is specified (for historical layers), use fixed radius bounds
    if (fixedRadiusKm !== undefined) {
      const latDelta = fixedRadiusKm / 111;
      const lonDelta = fixedRadiusKm / (111 * Math.cos((lat * Math.PI) / 180));

      return {
        center: [lon, lat],
        bounds: {
          xmin: lon - lonDelta,
          ymin: lat - latDelta,
          xmax: lon + lonDelta,
          ymax: lat + latDelta,
        },
      };
    }

    // Otherwise, use actual field bounds (for zoomToMission)
    // Convert extent to lon/lat bounds
    const [xmin3857, ymin3857, xmax3857, ymax3857] = fieldExtent;
    const [xmin, ymin] = toLonLat([xmin3857, ymin3857]);
    const [xmax, ymax] = toLonLat([xmax3857, ymax3857]);

    return {
      center: [lon, lat],
      bounds: { xmin, ymin, xmax, ymax },
    };
  }

  /**
   * Load historical map layers for this mission
   * @param historicalLayersService - Optional service instance to use, creates new if not provided
   * @param radiusKm - Radius in km for fixed bounds (default: 1km = 2x2km area)
   * @param yearsToPreload - Number of years to preload (default: 5)
   */
  async loadHistoricalMapLayers(
    historicalLayersService?: HistoricalLayersService,
    forceRefresh: boolean = false,
    radiusKm: number = 1,
    yearsToPreload: number = 5,
  ): Promise<void> {
    try {
      console.log('Loading historical layers for mission:', this.job_id);

      // Get mission center and bounds
      const { center, bounds } = this.getMissionCenter({ fixedBoundsRadiusKm: radiusKm });
      const [lon, lat] = center;

      // Use provided service or create new instance
      const service = historicalLayersService || new HistoricalLayersServiceImpl();

      // Preload with the job ID as cache key
      await service.updateAvailableYears(lon, lat, bounds, yearsToPreload, this.job_id, forceRefresh);

      dispatchMissionHistoricalMapLayersLoaded();

      console.log(`✓ Successfully loaded historical layers for mission ${this.job_id}`);
    } catch (error) {
      console.error('Error loading historical layers for mission:', error);
      // Don't throw - this is a background operation
    }
  }

  getBoundariesThatGoToKml() {
    const out: IKMLElement[] = [];

    for (const boundary of cleanSet(
      orderBy(
        this.getZones().filter(
          (sel) => sel.zone_type !== ZoneType.SAMPLE_ZONE && sel.zone_type !== ZoneType.COLLECTED_FIELD,
        ),
        ['zone_name'],
        ['asc'],
      ),
    )) {
      const boundary_kml = boundary.to_kml();

      if (boundary_kml && boundary_kml.length > 0) {
        for (const bnd of boundary_kml) {
          out.push(bnd);
        }
      }
    }

    return out;
  }

  async getXagKml() {
    try {
      const kml = this.to_kml({
        insert_boxes: false,
      });

      if (!kml) {
        return;
      }

      const data = kml.toString();

      if (!data) {
        return;
      }

      if (!this.job_id) {
        return;
      }

      const jobFromAt = await Job.getJobRecordById(this.job_id, true);

      if (!jobFromAt) {
        alertWarn('Could not fetch job from AT');

        return;
      }

      // replicating the formula from "Reduced Job Name with Job ID", which has been deleted
      const name = (jobFromAt.get('Name') ?? '') as string;
      const clientSimpleName = (jobFromAt.get('Client Simple Name for Job Name') ?? '') as string;
      const rogoJobId = (jobFromAt.get('Rogo Job ID') ?? '') as string;
      const marker = `__${clientSimpleName}__`;
      const markerIdx = clientSimpleName ? name.indexOf(marker) : -1;
      const reducedJobNameWithJobId =
        markerIdx >= 0 ? name.slice(0, markerIdx + marker.length) + rogoJobId : name + rogoJobId;
      const converter = new KmlToXagConverter([{ fieldName: reducedJobNameWithJobId, kmlData: data }]);

      return converter.convert();
    } catch (err) {
      console.error('Could not get xag', err);

      return undefined;
    }
  }

  getPrimarySpec(): SamplingSpec {
    const specs = this.getSamplingSpecs();

    if (specs.length === 0) {
      throw new Error(`Mission does not have any sampling specs`);
    }

    // if there's only one spec, it will be primary
    if (specs.length === 1) {
      return specs[0];
    }

    // if there are multiple specs, return the first one that is marked as primary
    const primarySpecs = specs.filter((spec) => spec.primary_spec);

    if (primarySpecs.length > 1) {
      throw new Error(`Mission ${this.instance_id} has multiple primary specs`);
    }

    if (primarySpecs.length === 1) {
      return primarySpecs[0];
    }

    return specs[0];
  }

  /**
   * If spec has lab_primary_delivery set, its per-lab details were populated
   * from a lab record and are authoritative. Otherwise fall back to Job values.
   * @param spec - specific spec to check; defaults to primary spec
   */
  getEffectiveLabQrsRequired(spec?: SamplingSpec): boolean {
    const target = spec ?? this.getSamplingSpecs()?.[0];

    if (target?.lab_primary_delivery) {
      return target.lab_qrs_required;
    }

    return this.getJob()?.lab_qrs_required ?? false;
  }

  getEffectiveLabPrimaryDelivery(spec?: SamplingSpec): string {
    const target = spec ?? this.getSamplingSpecs()?.[0];

    if (target?.lab_primary_delivery) {
      return target.lab_primary_delivery;
    }

    return this.getJob()?.lab_primary_delivery ?? '';
  }

  /** True only if ALL specs agree on auto_renumber. Falls back to Job when specs have no per-lab details. */
  autoRenumberingAllowedBySpecs(): boolean {
    const specs = this.getSamplingSpecs();
    const populated = specs.filter((s) => !!s.lab_primary_delivery);

    if (populated.length > 0) {
      return populated.every((s) => s.lab_auto_renumber);
    }

    return this.getJob()?.auto_renumber ?? false;
  }

  getSamplingSpecs() {
    if (this._refs && this._refs.SamplingSpec) {
      return new SamplingSpecList(
        ...Array.from(this._refs.SamplingSpec)
          .map((id) => SamplingSpec.get(id))
          .filter((sel) => !!sel),
      );
    } else {
      const samplingspecs = SamplingSpec.query((sel) => sel && sel.Mission_id === this.instance_id);

      for (const samplingspec of samplingspecs) {
        samplingspec.Mission_id = this.instance_id; // re-set foreign key to force update of _refs
      }

      return new SamplingSpecList(...samplingspecs);
    }
  }

  getSamplingSpecBySpecId(specId: string): SamplingSpec | undefined {
    return this.getSamplingSpecs().find((spec) => spec.spec_id === specId);
  }

  getJob() {
    if (this._refs && this._refs.Job) {
      const result = Array.from(this._refs.Job).map((id) => Job.get(id));

      return result[0];
    } else {
      const job = Job.findOne((sel) => !!sel && sel.Mission_id === this.instance_id);

      if (job) {
        job.Mission_id = this.instance_id; // re-set foreign key to force update of _refs
      }

      return job;
    }
  }

  getZoneRecordings() {
    if (this._refs && this._refs.ZoneRecording) {
      return new ZoneRecordingList(
        ...Array.from(this._refs.ZoneRecording)
          .map((id) => ZoneRecording.get(id))
          .filter((sel) => !!sel),
      );
    } else {
      const zonerecordings = ZoneRecording.query((sel) => sel && sel.Mission_id === this.instance_id);

      for (const zonerecording of zonerecordings) {
        zonerecording.Mission_id = this.instance_id; // re-set foreign key to force update of _refs
      }

      return new ZoneRecordingList(...zonerecordings);
    }
  }

  getSession() {
    if (this.#Session_id) {
      return Session.get(this.Session_id);
    }

    if (this._refs && this._refs.Session) {
      const result = Array.from(this._refs.Session).map((id) => Session.get(id));

      return result[0];
    } else {
      const session = Session.findOne((sel) => !!sel && sel.Mission_id === this.instance_id);

      if (session) {
        session.Mission_id = this.instance_id; // re-set foreign key to force update of _refs
      }

      return session;
    }
  }

  set Session_id(value) {
    if (this.#Session_id) {
      const relateObj = Session.get(this.#Session_id);

      if (relateObj) {
        relateObj.removeRelationshipData('Mission', this.instance_id);
      }
    }

    this.#Session_id = value;

    if (value) {
      const relateObj = Session.get(value);

      if (relateObj) {
        relateObj.addRelationshipData('Mission', this.instance_id);
      }
    }

    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  get Session_id() {
    return this.#Session_id;
  }

  async checkIntegrity() {
    const problems: string[] = [];
    // check uniqueness
    // check ID1
    const id1_duplicates = Mission.query(
      (sel) =>
        sel.instance_id !== this.instance_id && sel.name === this.name && sel.last_modified === this.last_modified,
    );

    for (const dup of id1_duplicates) {
      problems.push(
        `Duplicate mission found with ID1 for instance id ${this.instance_id}: ${dup.instance_id} (${dup})`,
      );
    }

    // check ID2
    const id2_duplicates = Mission.query(
      (sel) =>
        sel.instance_id !== this.instance_id && sel.job_id === this.job_id && sel.last_modified === this.last_modified,
    );

    for (const dup of id2_duplicates) {
      problems.push(
        `Duplicate mission found with ID2 for instance id ${this.instance_id}: ${dup.instance_id} (${dup})`,
      );
    }

    // check relationships
    for (const tableName in this._refs) {
      Array.from(this._refs[tableName]).forEach((key) => {
        if (!getMemoryTable(tableName)?.getOne(key)) {
          problems.push(`mission: Could not find ${tableName} instance for ID: ${key}`);
        }
      });
    }

    if (!this.getJob()) {
      problems.push(`mission: Could not find job instance across unconditional relationship R24: ${this.instance_id}`);
    }

    if (!this.getSession()) {
      problems.push(
        `mission: Could not find session instance across unconditional relationship R510: ${this.instance_id}`,
      );
    }

    return problems;
  }

  assign_id() {
    return assign_id.bind(this)();
  }

  to_kml({ insert_boxes = true, use_pulled_locations = false } = {}): KMLDocument | undefined {
    return to_kml.bind(this)({
      insert_boxes,
      use_pulled_locations,
    });
  }

  static async load_kml(text: string, jobId?: string, { sampling = false, ignore_loading_errors = false } = {}) {
    const missionKmlLoader = new MissionKmlLoader();

    return await missionKmlLoader.load(text, jobId, { sampling, ignore_loading_errors });
  }

  async reorder(coords: Coordinate[], renumber: boolean, sampling: boolean, reorderCentroids?: Set<CoordinatePoint>) {
    const pathReorderer = new PathReorderer(this, coords, renumber, sampling);

    return pathReorderer.reorder();
  }

  interLeavingSamples(): string[] {
    const cores = this.getPendingCores().sort(SoilCore.sortByWaypointNumber);

    const interLeavingSamples: Set<string> = new Set<string>();
    const visitedSamples: string[] = [];
    let prevSampleId: string = '';

    for (const core of cores) {
      const currentSampleId = core.sample_id;

      if (!currentSampleId) {
        continue;
      }

      if (!prevSampleId) {
        prevSampleId = currentSampleId;
      }

      if (visitedSamples.includes(currentSampleId)) {
        interLeavingSamples.add(currentSampleId);
      }

      if (prevSampleId !== currentSampleId) {
        visitedSamples.push(prevSampleId);
        prevSampleId = currentSampleId;
      }
    }

    return Array.from(interLeavingSamples).sort((a, b) => {
      if (isNumeric(a) && isNumeric(b)) {
        return parseInt(a) - parseInt(b);
      }

      return a.localeCompare(b);
    });
  }

  async buildSmartPath(
    startLat: number,
    startLon: number,
    endLat: number,
    endLon: number,
    parameters: {
      preferredAngleDegrees: number;
      preferredAnglePenaltyFactor: number;
      gridCellSizeMeters: number;
      borderClusterPenaltyFactor: number;
      zoneClusterPenaltyFactor: number;
      keepCurrentIntermediateWaypoints: boolean;
      minDistanceToBoundaryMeters: number;
      turnRadiusMeters: number;
      cruiseSpeedMps: number;
      turnSpeedFactor: number;
      sharpTurnWeight: number;
      dgpIterationCap: number;
      hybridAstarNodeCap: number;
      hybridAstarTimeBudgetMs: number;
      debugGeoJson: DebugGeoJsonMode;
    },
  ): Promise<SmartPathBuildResult> {
    const isZoneMission = this.is_zone_mission();

    // Grid missions: pathfinder receives the first + last pending core of
    // each sample (pair-of-cores, Apr 2026). Bare missions have samples
    // with no cores yet — run a one-time spec layout so SmartPathBuilder
    // has cores to read.
    if (!isZoneMission) {
      const samplesWithoutCores = this.getPendingSamples().filter(
        (s) => s.getSoilCores().filter((c) => c.isPending()).length === 0,
      );

      if (samplesWithoutCores.length > 0) {
        await logger.log('MissionClass - buildSmartPath - auto-layout for samples without cores');
        await this.calculate_path_restore_cores([]);
      }
    }

    const builder = new SmartPathBuilder(this);

    const result = await builder.build(startLat, startLon, endLat, endLon, parameters);

    if (!result.path) {
      return result;
    }

    const { path } = result;

    // Pathfinder echoes sampleId on every nav-visit (utils.cpp); no need
    // to derive it client-side. Sample.order assignment + (for zone)
    // core renumbering happen inside the apply* functions, AFTER the
    // tour walk — so this branch's only job is to dispatch.
    if (isZoneMission) {
      // Zone: regenerate extras AFTER each original on the bearing
      // toward the next tour point — preserves originals' positions
      // (cluster centres) and aligns extras with pathfinder's chosen
      // exit curve.
      await logger.log('MissionClass - buildSmartPath - applyZoneSmartPathTourToExistingCores');
      await this.applyZoneSmartPathTourToExistingCores(path);
    } else {
      // Grid: cores already exist (auto-layout above guarantees this).
      // Apply pathfinder's tour by reordering existing cores' waypoints —
      // do NOT regenerate cores via the spec, which would re-pick the
      // line orientation based on prev/next neighbours and could rotate
      // or flip the line, invalidating pathfinder's cost matrix.
      await logger.log('MissionClass - buildSmartPath - applySmartPathTourToExistingCores');
      await this.applySmartPathTourToExistingCores(path);
    }

    this.alertIfPathInterleavingForRegularZone();

    return result;
  }

  alertIfPathInterleavingForRegularZone(): void {
    if (!this.is_zone_mission()) {
      return;
    }

    if (this.is_zone_interleave_mission()) {
      return;
    }

    const interLeavingSamples = this.interLeavingSamples();

    if (interLeavingSamples.length) {
      alertWarn(
        `The path has interleaving zones: ${interLeavingSamples.join(', ')}. Please, try to build a new path with different parameters or reorder cores manually.`,
      );
    }
  }

  /** Last zone the robot was in — checked first to skip the full scan. */
  #lastEnclosedZone?: SampleZone;

  getEnclosedSampleZone(coordinate: Coordinate): SampleZone | undefined {
    // Fast path: if the robot is still in the same zone as last tick,
    // skip the O(Z) point-in-polygon scan over all sample zones.
    if (this.#lastEnclosedZone?.getZone()?.contains(coordinate[0], coordinate[1])) {
      return this.#lastEnclosedZone;
    }

    const enclosedZones = this.getAllEnclosedSampleZones(coordinate);

    if (enclosedZones.length === 0) {
      this.#lastEnclosedZone = undefined;

      return undefined;
    }

    if (enclosedZones.length > 1) {
      console.warn(`Found ${enclosedZones.length} zones containing coordinate ${coordinate}`);
    }

    this.#lastEnclosedZone = enclosedZones[0];

    return enclosedZones[0];
  }

  getAllEnclosedSampleZones(coordinate: Coordinate): SampleZone[] {
    const allSampleZones = this.getZones().getSampleZones();
    const enclosedSampleZones = allSampleZones.filter((sampleZone) =>
      sampleZone.getZone()?.contains(coordinate[0], coordinate[1]),
    );

    if (enclosedSampleZones.length === 0) {
      return [];
    }

    return enclosedSampleZones;
  }

  /** Origin-threshold cache for getCloseCores. If the robot moved
   *  less than 1/4 of the tolerance since the last call AND no core
   *  mutations occurred, the same cores are close — skip the full
   *  distance scan. */
  #closeCoresCache?: {
    result: CloseCore[];
    origin: Coordinate;
    tolerance: number;
    soilCoreMutations: number;
  };

  getCloseCores(coordinate: Coordinate, toleranceInMeters: number, coresToConsider?: SoilCore[]): CloseCore[] {
    const soilCoreTable = getMemoryTable('SoilCore');
    const currentMutations = soilCoreTable?.mutationCount ?? 0;

    if (
      this.#closeCoresCache &&
      this.#closeCoresCache.tolerance === toleranceInMeters &&
      this.#closeCoresCache.soilCoreMutations === currentMutations
    ) {
      // Check if origin barely moved — if within 25% of tolerance,
      // the set of close cores is the same.
      const threshold = Math.min(toleranceInMeters * 0.25, 1); // at most 1m (cores ~1m apart on a 20ft line)
      const dLat = Math.abs(coordinate[0] - this.#closeCoresCache.origin[0]);
      const dLon = Math.abs(coordinate[1] - this.#closeCoresCache.origin[1]);
      // Rough degrees: 1° lat ≈ 111km, 1° lon ≈ 85km
      const movedMeters = Math.max(dLat * METERS_PER_DEG_LAT, dLon * METERS_PER_DEG_LON);

      if (movedMeters < threshold) {
        return this.#closeCoresCache.result;
      }
    }

    const cores: SoilCore[] = coresToConsider !== undefined ? coresToConsider : this.getSamples().getSoilCores();

    // Bounding-box pre-filter + Haversine distance, inlined from the
    // old standalone getCloseCores in utils.ts.
    const latThreshold = toleranceInMeters * DEG_PER_METER_LAT * 1.1;
    const lonThreshold = toleranceInMeters * DEG_PER_METER_LON * 1.1;
    const [originLat, originLon] = coordinate;

    const filtered = cores
      .map((core) => {
        // Fast reject: if the lat/lon delta exceeds the bounding box,
        // the core is guaranteed to be outside tolerance. ~20x cheaper
        // than the full Haversine.
        if (Math.abs(core.lat - originLat) > latThreshold || Math.abs(core.lon - originLon) > lonThreshold) {
          return null;
        }

        const dist = distanceMeters(originLat, originLon, core.lat, core.lon);

        return dist < toleranceInMeters ? ([core, dist, true] as const) : null;
      })
      .filter((e): e is readonly [SoilCore, number, true] => e !== null);

    const result = filtered.sort((a, b) => a[1] - b[1]);

    this.#closeCoresCache = {
      result,
      origin: coordinate,
      tolerance: toleranceInMeters,
      soilCoreMutations: currentMutations,
    };

    return result;
  }

  // getCloseCoresZones = (coordinate: Coordinate, toleranceInMeters = REORDER_TOLERANCE_METERS) => {
  //   const cores = this.getZones().getSampleZones().getSampleSites().
  //   const closeCores = cores.map(core => {
  //       const distanceToCore = distanceLatLon(coordinate[0], coordinate[1], core.lat, core.lon, 'K') * 1000;

  //       return [core, distanceToCore, distanceToCore < toleranceInMeters] as const;
  //     }).filter(coreTuple => coreTuple[2]); // filter out cores that are too far away

  //   // calculate this outside of the return for benchmarking reasons
  //   const result = closeCores
  //     .sort((coreStructA, coreStructB) => coreStructA[1] - coreStructB[1])
  //     .map(core => core[0]);

  //   //console.log(`${closeCores.length} close cores found, took ${performance.now() - start}ms`);

  //   return result;
  // }

  getCloseSampleCentroids = (
    coordinate: Coordinate,
    { toleranceInMeters = REORDER_TOLERANCE_METERS, sort = true } = {},
  ) => {
    // get sample centroids
    const sampleCentroids = this.getSampleSites().getSampleCentroids();
    const closeSampleCentroids = sampleCentroids
      .map((sampleCentroid) => {
        const distanceInMeters =
          distanceLatLon(coordinate[0], coordinate[1], sampleCentroid.lat, sampleCentroid.lon, 'Kilometers') * 1000;

        return [sampleCentroid, distanceInMeters, distanceInMeters < toleranceInMeters] as const;
      })
      .filter((sampleCentroidTuple) => sampleCentroidTuple[2]); // filter out centroids that are too far away

    if (!sort) {
      const result = closeSampleCentroids.map((sampleCentroid) => sampleCentroid[0]);

      return result;
    }

    // calculate this outside of the return for benchmarking reasons
    const result = closeSampleCentroids
      .sort((sampleCentroidStructA, sampleCentroidStructB) => sampleCentroidStructA[1] - sampleCentroidStructB[1])
      .map((sampleCentroid) => sampleCentroid[0]);

    return result;
  };

  unskipAllSkipped(this: Mission) {
    return unskipAllSkipped.bind(this)();
  }

  skipUnsampled(this: Mission, change_type: SampleChangeType, change_reason: string) {
    return skipUnsampled.bind(this)(change_type, change_reason);
  }

  async skipMultipleSamples(samples: Sample[], changeType: SampleChangeType, changeReason: string) {
    for (const sample of samples) {
      if (sample.bag_id || sample.skipped_or_deleted) {
        continue;
      }

      sample.change_type = changeType;
      sample.change_reason = changeReason;

      for (const wp of this.getWaypoints()) {
        if (wp.getCorePoint()?.getSoilCore()?.Sample_id === sample.instance_id) {
          wp.dispose();
        }
      }

      sample.resetPulled();
    }

    await logger.log('mission_ops - skipMultipleSamples - calculate_path_restore_cores');
    await this.calculate_path_restore_cores([], null, false, true, true);
  }

  async unskipMultipleSamples(samples: Sample[]) {
    for (const sample of samples) {
      sample.unskip();
    }

    await logger.log('mission_ops - unskipMultipleSamples - calculate_path_restore_cores');
    await this.calculate_path_restore_cores([], null, false, true, true);
  }

  getCoresTaken(this: Mission) {
    return getCoresTaken.bind(this)();
  }

  async get_airtable_job_record(): Promise<LocalAtRecord<JobsFieldSet> | undefined> {
    if (!this.job_id) {
      return;
    }

    let record = LocalAtRecord.getRecord<JobsFieldSet>('Jobs', this.job_id);

    if (!record) {
      const search = await getLocalAtSearch<JobsFieldSet>(
        'Jobs',
        'App View - Run Missions',
        `{Rogo Job ID} = '${this.job_id}'`,
      );
      await search.refresh();

      record = LocalAtRecord.getRecord('Jobs', this.job_id);
    }

    return record;
  }

  async calculate_path_restore_cores(
    reordered_points_of_interest?: Sample[] | SoilCore[],
    initial_prev_point: Coordinate | null = null,
    matchCoresByPosition: boolean = false,
    keepIntermediateWaypoints: boolean = true,
    preserveExtras: boolean = false,
  ) {
    return await calculate_path_restore_cores.bind(this)(
      reordered_points_of_interest,
      initial_prev_point,
      matchCoresByPosition,
      keepIntermediateWaypoints,
      preserveExtras,
    );
  }

  async applySmartPathTourToExistingCores(navigationPoints: NavigationPoint[]) {
    return await applySmartPathTourToExistingCores.bind(this)(navigationPoints);
  }

  async applyZoneSmartPathTourToExistingCores(navigationPoints: NavigationPoint[]) {
    return await applyZoneSmartPathTourToExistingCores.bind(this)(navigationPoints);
  }

  async calculate_path(
    reordered_points_of_interest?: Sample[] | SoilCore[],
    initial_prev_point: Coordinate | null = null,
    intermediateWaypointsMap: IntermediateWaypointsMap = {},
    preserveExtras: boolean = false,
  ) {
    return await calculate_path.bind(this)(
      reordered_points_of_interest,
      initial_prev_point,
      intermediateWaypointsMap,
      preserveExtras,
    );
  }

  async reverse_path() {
    return await reverse_path.bind(this)();
  }

  async generate_lab_csvs() {
    return generate_lab_csvs.bind(this)();
  }

  async create_zone_cores(coords: Coordinate[]) {
    return await create_zone_cores.bind(this)(coords);
  }

  /**
   * Auto-place `spec.cores` SoilCores into every SAMPLE_ZONE.
   *
   * Delegates to {@link AutoCorePlacer}. Caller (DrawButton) is responsible
   * for confirm-then-wipe and calling `calculate_path()` afterwards.
   *
   * `options` overrides the kernel's baked defaults — used by the dev tuning panel.
   */
  async autoPlaceCores(options?: AutoCorePlacerOptions) {
    return await new AutoCorePlacer(this, options).placeAll();
  }

  /**
   * Compute the equal-area placement grids (grid samples + per-cell assignment)
   * for every SAMPLE_ZONE without placing cores — for the Technician grid overlay.
   */
  computeCoreGrids(options?: AutoCorePlacerOptions): CoreGridSegment[] {
    return new AutoCorePlacer(this, options).computeGrids();
  }

  delete_all_samples_and_cores() {
    const samples = this.getAllSamples(true);

    this.delete_samples_and_cores(samples);
  }

  delete_samples_and_cores(samples: Sample[]) {
    // Snapshot sample_ids BEFORE dispose — after disposal the sample
    // records are gone and we can't read them.
    const deletedSampleIds = samples.map((s) => s.sample_id);

    for (const sample of samples) {
      for (const core of sample.getSoilCores()) {
        core.dispose();
      }

      const sampleSite = sample.getSampleSite();

      if (sampleSite) {
        sampleSite.dispose();
      }

      sample.dispose();
    }

    dispatchMissionUpdated();
    // Targeted rebuild: CorePointsLayer / CentroidsLayer.rebuildForSamples
    // handle deletions by removing the stale featuresBySampleId entries.
    dispatchSamplesUpdated(deletedSampleIds);
  }

  delete_zone_cores() {
    const cores = this.getZones().getSampleZones().getSampleSites().getSamples().getSoilCores();

    for (const core of cores) {
      core.dispose();
    }

    dispatchMissionUpdated();
    dispatchCoresNeedUpdate();
  }

  getCentroidPath(): { coord: Coordinate; sample_id: string }[] {
    return getCentroidPath.bind(this)();
  }

  async createNewRegularZone(
    this: Mission,
    featureType: KmlFeatureType,
    points: Coordinate[][],
  ): Promise<Zone | undefined> {
    if (!['unsafe', 'slow', 'field', 'pullin', 'collected field', 'pullin_zone'].includes(featureType.toString())) {
      return undefined;
    }

    const [zone] = await Zone.new_zone(featureType, points, this.instance_id);

    await zone.normalize();

    return zone;
  }

  async updateOrDeleteBoundary(polyId: number, points: Coordinate[][], removeGeoLocatedCoresInside: boolean = true) {
    await update_or_delete_boundary.bind(this)(polyId, points, removeGeoLocatedCoresInside);

    saveDbToRecoveryServer(DB_SAVE_REASONS.BOUNDARY_UPDATED).catch((err) => {
      console.error('Could not save DB to recovery server after boundary update', err);
    });
  }

  getZoneByPolyId(polyId: number): Zone | undefined {
    const missionOuterBoundaries = cleanSet(flatten(this.getZones().map((zone) => zone.getOuterZoneBoundarys())));
    const zoneOuterBoundary = missionOuterBoundaries.find((sel) => sel.poly_id === polyId);

    if (!zoneOuterBoundary) {
      return;
    }

    return zoneOuterBoundary.getOuterZone();
  }

  // TODO this is NOT  coordinate array, it is some kind of different object with numbers and IDs..
  async update_points(
    points: {
      id: string;
      coords: { lat: number; lon: number };
    }[],
    sampling: boolean,
  ) {
    const pointsUpdater = new PointsUpdater(this, sampling);

    return await pointsUpdater.update(points);
  }

  async update_path(points: number[][]) {
    await update_path.bind(this)(points);
  }

  async to_shapefile(pts: boolean, bnd: boolean) {
    return await to_shapefile.bind(this)(pts, bnd);
  }

  to_pdf(logo: string | ArrayBuffer, pdfSampleOrder: string, config: PRINTER_CONFIG) {
    return to_pdf.bind(this)(logo, pdfSampleOrder, config);
  }

  _to_shapefile(pts: boolean, bnd: boolean) {
    return _to_shapefile.bind(this)(pts, bnd);
  }

  _to_kml({ use_pulled_locations = false, insert_boxes = true, safe_mode = false }): KMLDocument | undefined {
    return _to_kml.bind(this)({
      use_pulled_locations,
      insert_boxes,
      safe_mode,
    });
  }

  get_present_tolerance(unit: 'm' | 'ft' = 'm') {
    const missionTolerance = this.getJob()?.sampling_tolerance_ft;
    const presentToleranceFeet = missionTolerance || DEFAULT_CORE_PRESENT_DISTANCE_FEET;
    const presentToleranceMeters = presentToleranceFeet * M_PER_FT;

    return unit === 'm' ? presentToleranceMeters : presentToleranceFeet;
  }

  get_close_tolerance() {
    const presentToleranceMeters = this.get_present_tolerance();
    const closeSampleToleranceMeters = Math.max(presentToleranceMeters, DEFAULT_CORE_CLOSE_DISTANCE_METERS);
    // console.log(`Close Sample Tolerance: ${closeSampleToleranceMeters},${presentToleranceMeters},${DEFAULT_CORE_CLOSE_DISTANCE_METERS}`);

    return closeSampleToleranceMeters;
  }

  /** Cached with Job.mutationCount — invalidated when
   *  update_job_with_deal changes sampling_tolerance_ft. */
  #closePresentCache?: { result: readonly [number, number]; jobMutations: number };

  get_close_present_tolerance() {
    const jobTable = getMemoryTable('Job');

    if (this.#closePresentCache && this.#closePresentCache.jobMutations === (jobTable?.mutationCount ?? 0)) {
      return this.#closePresentCache.result;
    }

    const presentToleranceMeters = this.get_present_tolerance();
    const closeSampleToleranceMeters = Math.max(presentToleranceMeters, DEFAULT_CORE_CLOSE_DISTANCE_METERS);
    const result = [closeSampleToleranceMeters, presentToleranceMeters] as const;
    this.#closePresentCache = { result, jobMutations: jobTable?.mutationCount ?? 0 };

    return result;
  }

  to_ros_msg(frameID: FrameID = 'latlon') {
    return to_ros_msg.bind(this)(frameID);
  }

  update_pullin(point: Coordinate) {
    update_pullin.bind(this)(point);
  }

  update_parking_point(point: Coordinate) {
    update_parking_point.bind(this)(point);
  }

  update_starting_point(point: Coordinate) {
    update_starting_point.bind(this)(point);
  }

  update_end_point(point: Coordinate) {
    update_end_point.bind(this)(point);
  }

  async deleteWithDependencies() {
    this.dispose();
  }

  /** Cached with SamplingSpec.mutationCount. */
  #isZoneMissionCache?: { result: boolean; specMutations: number };

  is_zone_mission(): boolean {
    const specTable = getMemoryTable('SamplingSpec');

    if (this.#isZoneMissionCache && this.#isZoneMissionCache.specMutations === (specTable?.mutationCount ?? 0)) {
      return this.#isZoneMissionCache.result;
    }

    const result = is_zone_mission.bind(this)();
    this.#isZoneMissionCache = { result, specMutations: specTable?.mutationCount ?? 0 };

    return result;
  }

  is_zone_interleave_mission(): boolean {
    if (!this.is_zone_mission()) {
      return false;
    }

    const job = this.getJob();

    if (!job) {
      return false;
    }

    return job.zone_interleave;
  }

  removeWaypoints(): void {
    const waypoints = orderBy(cleanSet(this.getWaypoints()), ['waypoint_number'], ['asc']);

    for (const wp of waypoints) {
      wp.dispose();
    }
  }

  getNextSampleZoneNumber(): number {
    const sampleZoneNumericNames = this.getZonesOfTypeSampleZone()
      .map((zone) => parseInt(zone.zone_name))
      .filter((num) => !isNaN(num));

    if (sampleZoneNumericNames.length === 0) {
      return 1;
    }

    return Math.max(...sampleZoneNumericNames) + 1;
  }

  // [lon, lat] is expected
  async generateSampleZone(coords: MultiPolygonCoords): Promise<{ zone: Zone | undefined; errorMessages: string[] }> {
    const newZoneName = this.getNextSampleZoneNumber().toString();
    const zone = Zone.create();
    zone.Mission_id = this.instance_id;
    zone.zone_name = newZoneName;
    zone.zone_type = ZoneType.SAMPLE_ZONE;

    let sampleSite = this.getSampleSites().find((sel) => sel.original_sample_id === newZoneName);

    if (!sampleSite) {
      sampleSite = this.createSampleSite(newZoneName);
      sampleSite.sample_site_source = 'Rogo Map Maker';
    }

    let sampleZone = sampleSite.getSampleZone();

    if (!sampleZone) {
      sampleZone = SampleZone.create();
      sampleZone.SampleSite_id = sampleSite.instance_id;
    }

    sampleZone.Zone_id = zone.instance_id;

    const processingErrors = await processMultipolygonCoords(this, coords, zone);

    if (!zone.hasValidBoundaries()) {
      dispatchMissionUpdated();
      dispatchZonesUpdated();

      return { zone, errorMessages: ['New zone has invalid boundaries'] };
    }

    // Geomatch points with zones
    // this.matchCoresWithZonesAndAlert();

    dispatchMissionUpdated();
    dispatchZonesUpdated();

    return { zone, errorMessages: processingErrors };
  }

  async generateSampleZoneFromZone(initialZone: Zone): Promise<{ zone: Zone | undefined; errorMessages: string[] }> {
    if (initialZone.zone_type === ZoneType.SAMPLE_ZONE) {
      return { zone: undefined, errorMessages: ['Already a sample zone'] };
    }

    return await this.generateSampleZone(initialZone.toMultiPolygon().geometry.coordinates);
  }

  async generateSampleZoneFromFieldBoundary(): Promise<{ zone: Zone | undefined; errorMessages: string[] }> {
    let boundary: Zone | undefined;

    try {
      boundary = this.getBoundary();
    } catch (error) {}

    if (!boundary) {
      return { zone: undefined, errorMessages: ['Mission boundary not found'] };
    }

    return this.generateSampleZoneFromZone(boundary);
  }

  addPolyToZone(polyId: number, zone: Zone): void {
    const polyManager = new PolyManager(this, zone);

    polyManager.addPolyToZone(polyId);
  }

  disposeBoundaryByPolyId(polyId: number): void {
    const allOuterBoundaries = cleanSet(flatten(this.getZones().map((zone) => zone.getOuterZoneBoundarys())));
    const outerBoundary = allOuterBoundaries.find((sel) => sel.poly_id === polyId);

    if (outerBoundary) {
      outerBoundary.dispose();
    }

    const allInnerBoundaries = cleanSet(flatten(this.getZones().map((zone) => zone.getInnerZoneBoundarys())));
    const innerBoundaries = allInnerBoundaries.filter((sel) => sel.poly_id === polyId);

    innerBoundaries.forEach((ib) => ib.dispose());
  }

  hasUndefinedSampleIds(): boolean {
    return this.getAllSamples().some((sample) => sample.isUndefinedSample());
  }

  matchCoresWithZones(): {
    coresNoSampleZone: string[];
    coresFailedToMove: string[];
    matchedCoresByZones: { [zoneId: number]: string[] };
  } {
    const result: {
      coresNoSampleZone: string[];
      coresFailedToMove: string[];
      matchedCoresByZones: { [zoneId: number]: string[] };
    } = {
      coresNoSampleZone: [] as string[],
      coresFailedToMove: [] as string[],
      matchedCoresByZones: {},
    };

    if (!this.is_zone_mission()) {
      return result;
    }

    this.getAllCores().forEach((core: SoilCore) => {
      const oldCoreName = core.name;
      result[oldCoreName] = { newSampleId: '', errorMessage: '', warningMessage: '' };

      const sampleZone = this.getEnclosedSampleZone([core.lat, core.lon]);

      if (!sampleZone) {
        result.coresNoSampleZone.push(oldCoreName);

        return;
      }

      const zone = sampleZone.getZone();

      if (!zone) {
        result.coresNoSampleZone.push(oldCoreName);

        return;
      }

      const outerZoneBoundaries = zone.getOuterZoneBoundarys();

      for (const boundary of cleanSet(outerZoneBoundaries)) {
        if (boundary.contains(core.lat, core.lon)) {
          const newSampleId = core.changeParentSampleBySampleZone(this, sampleZone);

          if (!newSampleId) {
            result.coresFailedToMove.push(oldCoreName);

            return;
          }

          if (!result.matchedCoresByZones[newSampleId]) {
            result.matchedCoresByZones[newSampleId] = [];
          }

          result.matchedCoresByZones[newSampleId].push(oldCoreName);

          return;
        }
      }
    });

    this.removeUndefinedSamplesIfNoMoreNeeded();

    this.removeSamplesWithNoCores();

    dispatchMissionUpdated();
    // Cores' parent sample_id changed (affects CorePointsLayer's
    // featuresBySampleId map keys). Step 13 of the KmlLayer split.
    dispatchCoresNeedUpdate();

    return result;
  }

  matchCoresWithZonesAndAlert(): void {
    if (!this.is_zone_mission()) {
      return;
    }

    const { coresNoSampleZone, coresFailedToMove, matchedCoresByZones } = this.matchCoresWithZones();

    if (coresFailedToMove.length) {
      alertWarn(`The followings core failed to move to new zone: ${coresFailedToMove.join(', ')}`);
    }

    if (coresNoSampleZone.length) {
      alertWarn(`Failed to match the following cores: ${coresNoSampleZone.join(', ')}`);
    }

    let matchMessage = '';
    Object.keys(matchedCoresByZones)
      .sort()
      .forEach((zoneId) => {
        matchMessage += `Matched the following cores to zone ${zoneId}: ${matchedCoresByZones[zoneId].join(', ')} ; `;
      });

    if (matchMessage) {
      alertSuccess(matchMessage);
    }
  }

  removeUndefinedSamplesIfNoMoreNeeded(): void {
    const undefinedSamples = this.getAllSamples().filter((sample) => sample.isUndefinedSample());

    if (undefinedSamples.length === 0) {
      return;
    }

    undefinedSamples.forEach((undefinedSample) => {
      const cores = undefinedSample.getSoilCores();

      if (cores.length > 0) {
        return;
      }

      // If we have no cores, we can safely remove the undefined sample ID.
      undefinedSample.dispose();
    });
  }

  getSamplesWithNoCores(): Sample[] {
    return this.getAllSamples(false).filter((s) => s.hasNoCores());
  }

  getSampleZonesWithNoCores(): Zone[] {
    return this.getZonesOfTypeSampleZone().filter((z) => {
      const sampleZone = z.getSampleZone();

      if (!sampleZone) {
        return true;
      }

      const sample = sampleZone.getSample();

      if (!sample) {
        return true;
      }

      if (sample.skipped_or_deleted) {
        return false;
      }

      return sample.hasNoCores();
    });
  }

  removeSamplesWithNoCores(): void {
    this.getAllSamples().forEach((sample) => {
      const cores = sample.getSoilCores();

      if (cores.length > 0) {
        return;
      }

      sample.dispose();
    });
  }

  removeInnerBoundary(innerBoundaryId: number) {
    const innerBoundary = cleanSet(flatten(this.getZones().map((zone) => zone.getInnerZoneBoundarys()))).find(
      (ib) => ib.instance_id === innerBoundaryId,
    );

    if (!innerBoundary) {
      return;
    }

    innerBoundary.dispose();

    this.to_ros_msg();
  }

  getOuterBoundaryById(outerBoundaryId: number): Boundary | undefined {
    const outerBoundary = cleanSet(flatten(this.getZones().map((zone) => zone.getOuterZoneBoundarys()))).find(
      (ob) => ob.instance_id === outerBoundaryId,
    );

    return outerBoundary;
  }

  getInnerBoundaryById(innerBoundaryId: number): Boundary | undefined {
    const innerBoundary = cleanSet(flatten(this.getZones().map((zone) => zone.getInnerZoneBoundarys()))).find(
      (ib) => ib.instance_id === innerBoundaryId,
    );

    return innerBoundary;
  }

  async removeZoneWithBoundaries(zoneInstanceId: number, removeGeoLocatedCoresInside: boolean = true): Promise<void> {
    const zone = this.getZones().find((z) => z.instance_id === zoneInstanceId);

    if (!zone) {
      return;
    }

    const outerBoundaries = zone.getOuterZoneBoundarys();

    if (outerBoundaries.length === 0) {
      // Handle case with invalid zone - no outer boundaries
      const innerBoundaries = zone.getInnerZoneBoundarys();

      if (innerBoundaries.length === 0) {
        zone.dispose();

        return;
      }

      // No outer boundaries - just remove inner boundaries
      for (const innerBoundary of innerBoundaries) {
        innerBoundary.dispose();
      }

      zone.dispose();

      return;
    }

    for (const boundary of outerBoundaries) {
      // Outer and inner boundary have the same poly_id
      const polyId = boundary.poly_id;

      switch (zone.zone_type) {
        case ZoneType.SAMPLE_ZONE:
        case ZoneType.FIELD:
        case ZoneType.UNSAFE:
        case ZoneType.SLOW:
        case ZoneType.PULLIN:
        case ZoneType.COLLECTED_FIELD:
          await this.removeOuterAndInnerBoundariesByPolyId(polyId, removeGeoLocatedCoresInside);

          break;
        default:
          break;
      }
    }
  }

  async removeOuterAndInnerBoundariesByPolyId(polyId: number, removeGeoLocatedCoresInside: boolean): Promise<void> {
    await this.updateOrDeleteBoundary(polyId, [], removeGeoLocatedCoresInside);
  }

  // Works for both cases: grid and zone missions
  // For grid missions, we use samples as edges between which
  // we generate intermediate waypoints.
  // For zone missions, we use cores as edges.
  removeWaypointsAndGenerateIntermediateWaypointsMap() {
    const intermediateWaypointsMap: IntermediateWaypointsMap = {};
    const waypoints = orderBy(cleanSet(this.getWaypoints()), ['waypoint_number'], ['asc']);

    if (waypoints.length === 0) {
      return intermediateWaypointsMap;
    }

    const isZoneMission = this.is_zone_mission();
    let prevId = '';
    let pointsBetween: Coordinate[] = [];

    // Loop through waypoints, dispose them and generate intermediate waypoints map
    for (const [index, wp] of waypoints.entries()) {
      const coreSampleId = wp.getCorePoint()?.getSoilCore()?.sample_id;
      const currentId = isZoneMission
        ? coreSampleId
          ? generateCoreIdentifierFromCoordinates(coreSampleId, [wp.lat, wp.lon])
          : undefined
        : wp.getCorePoint()?.getSoilCore()?.sample_id;

      if (index === 0) {
        wp.dispose();
        prevId = currentId ? currentId : prevId;

        continue;
      }

      // Having currentId means that we are at a sample or core waypoint
      if (currentId) {
        // If currentId differs from prevId, we are at a new sample or core waypoint:
        // we need to store the points between prevId and currentId
        if (currentId !== prevId) {
          if (pointsBetween.length > 0) {
            intermediateWaypointsMap[prevId] = { [currentId]: pointsBetween };
          }

          prevId = currentId;
          pointsBetween = [];
        }
      } else {
        // if currentId is undefined, we are at an intermediate waypoint
        pointsBetween.push([wp.lat, wp.lon] as Coordinate);
      }

      wp.dispose();
    }

    return intermediateWaypointsMap;
  }

  async update_mission_details({ sampling = false, loading_from_shapefiles = false } = {}) {
    return await update_mission_details.bind(this)({ sampling, loading_from_shapefiles });
  }

  static async generate_from_shapes(
    missionWithJob: Mission,
    shpfiles: RogoShapefile[],
    selectColumnFunction: (data: ConfirmColumn) => Promise<string>,
  ) {
    return await generate_from_shapes(missionWithJob, shpfiles, selectColumnFunction);
  }

  renumber_samples(sampling: boolean = true, recalculate_checksum = false) {
    return renumber_samples.bind(this)(sampling, recalculate_checksum);
  }

  async add_sample(point: { coords: { lat: number; lon: number } }, sample_id: string, sample_source: SampleSource) {
    return await add_sample.bind(this)(point, sample_id, sample_source);
  }

  async add_core(
    point: { coords: { lat: number; lon: number } },
    coreId: string,
    sampleSource: SampleSource,
  ): Promise<SoilCore | undefined> {
    return await add_core.bind(this)(point, coreId, sampleSource);
  }

  async rename_sample(oldSampleId: string, newSampleId: string) {
    await logger.log(`Mission::rename_sample: oldSampleId ${oldSampleId}, newSampleId ${newSampleId} `);

    const matchingSamples = this.getAllSamples().filter((sample) => sample.sample_id === oldSampleId);

    if (!matchingSamples.length) {
      throw new Error(`Could not find sample ${oldSampleId}`);
    }

    if (matchingSamples.length > 1) {
      throw new Error(`Found multiple samples with ID ${oldSampleId}`);
    }

    const sampleWithNewIdThatAlreadyExists = this.getAllSamples().find((s) => s.sample_id === newSampleId);

    // For grid missions - do not allow renaming to an ID that already exists.
    if (!this.is_zone_mission() && sampleWithNewIdThatAlreadyExists) {
      throw new Error(`Sample with id ${newSampleId} already exists`);
    }

    const sampleToRename = matchingSamples[0];

    if (sampleToRename.hasBarcode()) {
      throw new Error(`Sample with id ${sampleToRename.sample_id} already has a barcode, aborting.`);
    }

    if (sampleWithNewIdThatAlreadyExists && sampleWithNewIdThatAlreadyExists.hasBarcode()) {
      throw new Error(`Sample with id ${sampleWithNewIdThatAlreadyExists.sample_id} already has a barcode, aborting.`);
    }

    const sampleSite = sampleToRename.getSampleSite();
    sampleToRename.sample_id = newSampleId;

    const sampleZone = sampleSite?.getSampleZone()?.getZone();

    if (sampleZone) {
      sampleZone.zone_name = newSampleId;
    }

    this.mergeSampleZonesWithOneName();

    await logger.log(`Mission::rename_sample - end`);
  }

  getSampleZonesGroupedByName(): { [zoneName: string]: Zone[] } {
    return this.getZonesOfTypeSampleZone().reduce((acc, cur) => {
      if (!acc[cur.zone_name]) {
        acc[cur.zone_name] = [];
      }

      acc[cur.zone_name].push(cur);

      return acc;
    }, {});
  }

  getSampleZonesWithOneName(): { [zoneName: string]: Zone[] } {
    const zonesGroupedByName = this.getSampleZonesGroupedByName();

    return Object.keys(zonesGroupedByName)
      .filter((zoneName) => zonesGroupedByName[zoneName].length > 1)
      .reduce((acc, zoneName) => {
        acc[zoneName] = zonesGroupedByName[zoneName];

        return acc;
      }, {});
  }

  async mergeSampleZonesWithOneName() {
    const zonesWithOneName = this.getSampleZonesWithOneName();

    for (const zoneName in zonesWithOneName) {
      await logger.log(
        `mergeSampleZonesWithOneName start: zoneName ${zoneName}, zones number ${zonesWithOneName.length} `,
      );

      const zones = zonesWithOneName[zoneName];
      const gluedShape = GeoDataUtilities.glueShapes(zones.map((z) => z.toMultiPolygon()));

      // Create new zone
      const newZone = Zone.create();
      newZone.Mission_id = this.instance_id;
      newZone.zone_name = zoneName;
      newZone.zone_type = ZoneType.SAMPLE_ZONE;

      let geomCoordinates: Position[][][];

      if (gluedShape.geometry.type === 'Polygon') {
        geomCoordinates = [gluedShape.geometry.coordinates];
      } else {
        geomCoordinates = gluedShape.geometry.coordinates;
      }

      await processMultipolygonCoords(this, geomCoordinates, newZone);

      if (!newZone.hasValidBoundaries()) {
        alertError(`Zone ${newZone.zone_name} has invalid boundaries, aborting.`);
        newZone.dispose();

        continue;
      }

      // At this point we know that the zone is valid.
      const newSampleSite = this.createSampleSite(zoneName);
      newSampleSite.sample_site_source = 'Rogo Map Maker';

      const newSampleZone = SampleZone.create();
      newSampleZone.SampleSite_id = newSampleSite.instance_id;
      newSampleZone.Zone_id = newZone.instance_id;

      // Move samples from old zones to a new zone and dispose of old sample zones.
      for (const zone of zones) {
        const sampleZone = zone.getSampleZone();

        if (sampleZone) {
          sampleZone
            .getSample()
            ?.getSoilCores()
            .forEach((core) => core.changeParentSampleBySampleZone(this, newSampleZone));
        }
      }

      // Remove old zones + sample zones (inside zone.dispose)
      for (const zone of zones) {
        zone.dispose();
      }

      await logger.log(`mergeSampleZonesWithOneName - end`);
    }

    dispatchMissionUpdated();
    // Cores were reparented to a new sample (changeParentSampleBySampleZone)
    // above, which changes their sample_id map key in CorePointsLayer.
    dispatchCoresNeedUpdate();
  }

  getBarcodes() {
    return getBarcodes.bind(this)();
  }

  getMissionName(type: string): string {
    return getMissionName.bind(this)(type);
  }

  getLastScannedSampleId() {
    const samples = this.getAllSamples();

    if (samples.length === 0) {
      return '';
    }

    const lastScannedSample = samples.sort((sampleA, sampleB) => sampleB.scanned_at - sampleA.scanned_at);

    return lastScannedSample[0].sample_id;
  }

  getAllSamples(include_skips = true): Sample[] {
    return getAllSamples.bind(this)(include_skips);
  }

  getAllSampleBoxes(): SampleBox[] {
    return getAllSampleBoxes.bind(this)();
  }

  getSamplesWithNoBarcodes(): Sample[] {
    const samples = this.getAllSamples(false);

    return samples.filter((sample) => !sample.hasBarcode());
  }

  getPendingSamples(): Sample[] {
    return this.getAllSamples(false).filter((sample) => sample.isPending());
  }

  getPendingCores(): SoilCore[] {
    const samplesWithNoBarcodes = this.getSamplesWithNoBarcodes();

    return samplesWithNoBarcodes.map((sample) => sample.getPendingSoilCores()).flat();
  }

  // getNextNotFullyPulledSample() {
  //   const samples = this.getAllSamples(false);
  //   const notFullyPulled = samples.filter((sample) => !sample.allCoresPulled());
  //   if (notFullyPulled.length === 0) {
  //     return undefined;
  //   }

  //   const nextNotFullyPulled = notFullyPulled.sort((sampleA, sampleB) => sampleA.order - sampleB.order)[0];

  //   return nextNotFullyPulled;
  // }

  #nextUnscannedCache?: { result: Sample | undefined; sampleMutations: number; sampleSiteMutations: number };

  getNextUnscannedSample(): Sample | undefined {
    const sampleTable = getMemoryTable('Sample');
    const sampleSiteTable = getMemoryTable('SampleSite');

    if (
      this.#nextUnscannedCache &&
      this.#nextUnscannedCache.sampleMutations === (sampleTable?.mutationCount ?? 0) &&
      this.#nextUnscannedCache.sampleSiteMutations === (sampleSiteTable?.mutationCount ?? 0)
    ) {
      return this.#nextUnscannedCache.result;
    }

    const unscanned = this.getAllSamples(false).filter((s) => !s.hasBarcode());
    let result: Sample | undefined;

    if (unscanned.length === 0) {
      result = undefined;
    } else {
      result = unscanned.sort((sampleA, sampleB) => sampleA.order - sampleB.order)[0];
    }

    // Capture AFTER rebuild
    this.#nextUnscannedCache = {
      result,
      sampleMutations: sampleTable?.mutationCount ?? 0,
      sampleSiteMutations: sampleSiteTable?.mutationCount ?? 0,
    };

    return result;
  }

  isManualMeasurementRequired(): boolean {
    const job = this.getJob();

    if (!job) {
      return false;
    }

    return job.manual_measurement_required;
  }

  isBDMission(): boolean {
    const job = this.getJob();

    if (!job) {
      return false;
    }

    return job.sampling_type_special.includes('BD');
  }

  /**
   * Carbon-probe job. This — not `isBDMission()` — is the predicate that makes
   * `to_ros` export cores with command 3 instead of 1/2, which in turn excludes
   * them from the robot's core-to-core path type (`mapper.h::is_c2c`).
   * Single source of truth for both the ROS export and the Dubin's
   * reconstruction, so the two can never drift.
   */
  usesCarbonProbe(): boolean {
    return (this.getJob()?.core_diameter || '0.75 in') === '2 in';
  }

  isLoamMission(): boolean {
    const job = this.getJob();

    if (!job) {
      return false;
    }

    return job.client?.toLowerCase().includes('loam');
  }

  // Order of checks:
  // 1. If a starting point is placed on the map - use it.
  // 2. If a parking point is placed on the map - use it.
  // 3. If a pullin is placed on the map - use it.
  getStartingPosition(): { lat: number; lon: number } {
    if (this.starting_point.lat && this.starting_point.lon) {
      return this.starting_point;
    }

    if (this.parking_point.lat && this.parking_point.lon) {
      return this.parking_point;
    }

    const pullInPosition = { lat: this.pullin_lat, lon: this.pullin_lon };

    return pullInPosition;
  }

  // Order of checks:
  // 1. If an end point is placed on the map - use it.
  // 2. Use starting position
  getEndPosition(): { lat: number; lon: number } {
    if (this.end_point.lat && this.end_point.lon) {
      return this.end_point;
    }

    return this.getStartingPosition();
  }

  deleteAllExtraCores(): number[] {
    return deleteExtraCoresForSamples(this.getSamplesWithNoBarcodes());
  }

  static createMissionWithJob(jobId: string, missionName: string): Mission {
    // First delete stale missions
    Mission.query((sel) => sel.job_id === jobId).forEach((m) => m.deleteWithDependencies());

    // Then create a new one
    const mission = Mission.create();
    mission.job_id = jobId;
    mission.name = missionName;

    const job = Job.create();
    job.Mission_id = mission.instance_id;

    return mission;
  }
}

export class MissionList extends DataObjectList<Mission> {
  getSampleSites() {
    return new SampleSiteList(
      ...this.reduce((sampleSites: SampleSite[], mission) => sampleSites.concat(mission.getSampleSites()), []).filter(
        (sel) => !!sel,
      ),
    );
  }

  getWaypoints() {
    return new WaypointList(
      ...this.reduce((waypoints: Waypoint[], mission) => waypoints.concat(mission.getWaypoints()), []).filter(
        (sel) => !!sel,
      ),
    );
  }

  getZones() {
    return new ZoneList(
      ...this.reduce((zones: Zone[], mission) => zones.concat(mission.getZones()), []).filter((sel) => !!sel),
    );
  }

  getSamplingSpecs() {
    return new SamplingSpecList(
      ...this.reduce(
        (samplingSpecs: SamplingSpec[], mission) => samplingSpecs.concat(mission.getSamplingSpecs()),
        [],
      ).filter((sel) => !!sel),
    );
  }

  getJobs() {
    return new JobList(...this.map((mission) => mission.getJob()).filter((sel) => !!sel));
  }

  getZoneRecordings() {
    return new ZoneRecordingList(
      ...this.reduce(
        (zoneRecordings: ZoneRecording[], mission) => zoneRecordings.concat(mission.getZoneRecordings()),
        [],
      ).filter((sel) => !!sel),
    );
  }

  getSessions() {
    return new SessionList(...this.map((mission) => mission.getSession()).filter((sel) => !!sel));
  }
}
